import {
  useEffect,
  useState,
  useRef,
  useContext,
  useMemo,
  useCallback,
} from "react";

//Components
import VirtualizedAutocomplete from "../VirtualizedAutocomplete";
import {
  FormControl,
  TextField,
  TextareaAutosize,
  FormLabel,
  Box,
  Button,
  Modal,
  InputAdornment,
  FormHelperText,
} from "@material-ui/core";
import SingleDropDown from "../SingleDropDown";
import AddressInputAutoSuggest from "../AddressInputAutoSuggest";
import CheckboxWithLabel from "../CheckboxWithLabel";
import MultiDropDownObjects from "../MultiDropDownObjects";
import CustomAutoComplete from "../CustomAutoComplete";
import { KeyboardDatePicker } from "@material-ui/pickers";
import CloseIcon from "@material-ui/icons/Close";
import VCVC from "../VCVC/VCVC";
import ticketFormStyles from "../VCVC/TicketForm/TicketForm.module.css";
import warningFormStyles from "../VCVC/WarningForm/WarningForm.module.css";
import { getLoadedPrintStyles } from "./printStyles";
import DefendantDidThen from "../DefendantDidThen";

//Styles
import { useStyles } from "../../Styles/styles";
import clsx from "clsx";

//Fetching
import { useMutation, useApolloClient } from "@apollo/client";
import { REMOVE_TICKET_FROM_INCIDENT } from "../../Mutations/IncidentMutations/RemoveTicketFromIncident";
import { SUBMIT_CITATION } from "../../Mutations/SubmitCitation";

//Context
import { SetupContext } from "../../Contexts/SetupContext";
import { DashboardContext } from "../../Contexts/DashboardContext";
import { IncidentsContext } from "../../Contexts/IncidentsContext";
import { INCIDENT_ACTIONS } from "../../Actions/IncidentActions";
import { TICKET_ACTIONS } from "../../Actions/TicketActions";
import { MutationContext } from "../../Contexts/MutationContext";
import { UserContext } from "../../Contexts/UserContext";

//Utils
import {
  formatOfficerName,
  formatNameFromPersonObj,
  formatNameFromVehicleObj,
  parseObjectArrayIdString,
  isTicketWarning,
  isWarningNumberValid,
  isTicketNumberValid,
  isJuvenile,
  displayFedVio,
  displayCommercialFields,
  isMunicipalVio,
  localGetData,
  parseDateFlexible,
} from "../../utils/utils";
import { isSuperUser, shouldBeLocked, checkRoles } from "../../utils/Roles";
import { calculatePenalty } from "../../utils/penalty-calculation";
import { createEmptyTicket } from "../../utils/EmptyObjects";
import { useReactToPrint } from "react-to-print";
import _ from "lodash";
import { generateOfflineTicketNumber } from "../../utils/TicketExclusionList";
import RaceDataUof from "./RaceDataUoF";
import SpeedMeasurement from "./SpeedMeasurement";
import { checkTables } from "../../IndexedDb/tableFunctions";
import { db } from "../../IndexedDb/database";
import { getValueFromEventValue } from "../../utils/getValueFromEventValue";
import {
  useHandlePrint,
  useHandleTicketSubmission,
  useSetCurrentStatus,
} from "./hooks";
import {
  returnTicketValidationObj,
  UNDER_17_RELIEF_ACT_TYPE_NAME,
  isUnder17OnDate,
} from "./ticketValidation";
import LicenseNumberAndState from "../LicenseNumberAndState/LicenseNumberAndState";
import { ADD_AUDIT_LOG } from "../../Mutations/AddAuditLog";
import { SET_LAST_CONTACTED } from "../../Mutations/SetLastContacted";
import WrappedKeyboardDateTimePicker from "../WrappedKeyboardDateTimePicker";
import { normalizeTicketUpdatePayload } from "../../utils/ticketDispatchUtils";

import moment from "moment";

const stateToMutation = {
  radarIdStr: "radarIdStr",
  ticketNumber: "ticketNumber",
  isWarning: "isWarning",
  federalViolationCode: "federalViolationCodeId",
  cvHazardFee: "cvHazardFee",
  highway: "highwayId",
  cdl: "cdl",
  municipality: "municipalityId",
  ticketDeliveryType: "ticketDeliveryTypeId",
  issuedTo: "issuedToId",
  issuedToAddress: "issuedToAddressId",
  mailingAddressVerified: "mailingAddressVerified",
  officer: "officerId",
  trespassRequestor: "trespassRequestorId",
  code: "code",
  actualSpeed: "actualSpeed",
  postedLimit: "postedLimit",
  ticketStatus: "ticketStatusId",
  dispositionPenaltyAmount: "dispositionPenaltyAmount",
  defendantActions: "defendantActions",
  reliefActType: "reliefActTypeId",
  reliefActComment: "reliefActComment",
  defendantParent: "defendantParentId",
  deliveredToType: "deliveredToTypeId",
  municipalOrdinance: "municipalOrdinance",
  operator: "operatorId",
  vehicle: "vehicleId",
  perceivedRace: "perceivedRaceId",
  ticketStopType: "ticketStopTypeId",
  ticketSearchType: "ticketSearchTypeId",
  ticketEvidenceType: "ticketEvidenceTypeId",
  ticketOutcomeType: "ticketOutcomeTypeId",
  passengerTicketSearchType: "passengerTicketSearchTypeId",
  passengerTicketEvidenceType: "passengerTicketEvidenceTypeId",
  passengerTicketOutcomeType: "passengerTicketOutcomeTypeId",
  //Seat belt is weird and I don't have the options from setup. They're hard-coded.
  seatBeltOccurrence: "seatBeltOccurrence",
  accident: "accident",
  hazmat: "hazmat",
  fatality: "fatality",
  commercialVehicle: "commercialVehicle",
  relativeCrimeCharge: "relativeCrimeCharge",
  narrative: "narrative",
  weatherConditions: "weatherConditionIds",
  roadConditions: "roadConditionIds",
  trafficConditions: "trafficConditionIds",
  lightingConditions: "lightingConditionIds",
  //All of the pass/fails may just be passing 1 or 0. It may not need "id"
  radarTuningForksBefore: "radarTuningForksBeforeId",
  radarInternalCalibrationBefore: "radarInternalCalibrationBeforeId",
  radarSpeedometerVerifBefore: "radarSpeedometerVerifBeforeId",
  radarDisplayLightsBefore: "radarDisplayLightsBeforeId",
  radarTuningForksAfter: "radarTuningForksAfterId",
  radarInternalCalibrationAfter: "radarInternalCalibrationAfterId",
  radarSpeedometerVerifAfter: "radarSpeedometerVerifAfterId",
  radarDisplayLightsAfter: "radarDisplayLightsAfterId",
  laserIdStr: "laserIdStr",
  laserSelfBefore: "laserSelfBeforeId",
  laserScopeAlignmentBefore: "laserScopeAlignmentBeforeId",
  laserKnownDistanceBefore: "laserKnownDistanceBeforeId",
  laserDisplayLightsBefore: "laserDisplayLightsBeforeId",
  laserSelfAfter: "laserSelfAfterId",
  laserScopeAlignmentAfter: "laserScopeAlignmentAfterId",
  laserKnownDistanceAfter: "laserKnownDistanceAfterId",
  laserDisplayLightsAfter: "laserDisplayLightsAfterId",
  uofInstance: "uofInstanceId",
  uofInstanceTypes: "uofInstanceTypeIds",
  uofInjuryDetails: "uofInjuryDetailIds",
  ticketLocation: "ticketLocationId",
  violationDate: "violationDate",
  issuedAt: "issuedAt",
  trespassUntil: "trespassUntil",
  radarTimeBefore: "radarTimeBefore",
  radarTimeAfter: "radarTimeAfter",
  laserTimeBefore: "laserTimeBefore",
  laserTimeAfter: "laserTimeAfter",
  licenseState: "stateId",
  licenseNumber: "driversLicense",
  weightLimit: "weightLimitId",
  violationOccurrence: "violationOccurrenceId",
};

const pageStyle = (isWarning = false) => {
  if (isWarning) {
    return `
    @page {
      size: portrait;
    }
    `;
  } else {
    return `
    @page {
      size: landscape;
    }
    `;
  }
};

const TicketAndWarning = ({
  incidentIndex,
  incidentId,
  incidentUuid,
  ticket,
  ticketsData,
  ticketsLoading,
  ticketsError,
  piss,
  incidentPeople,
  incidentOfficers,
  viss,
  ticketTab,
  setTicketTab,
  ticketsLength,
  incidentOriId,
  primaryOfficer,
  inIncident = true,
  userRoles,
  disabled = undefined,
  setPausePolling,
  setPopper,
  disableTicketAddition,
  addTicketDisabled,
  hasBlankTicket,
  ...props
}) => {
  const memoizedLocked = useMemo(
    () => shouldBeLocked(disabled, props?.canEdit, userRoles),
    [disabled, props?.canEdit, userRoles],
  );
  const canSubmitToJb = useMemo(
    () => checkRoles(userRoles, "submit_to_jb"),
    [userRoles],
  );

  // const pidError = useMemo(() => {
  //   return isLicenseValid(ticket.driversLicense, ticket?.state?.name)
  // }, [ticket.driversLicense, ticket?.state?.name])
  const client = useApolloClient();
  const classes = useStyles();
  const { setup } = useContext(SetupContext);
  const { incidents, incidentDispatch, ticketDispatch } =
    useContext(IncidentsContext);
  const { indexedDb } = useContext(DashboardContext);
  const { user, incrementOfflineTicketNumber, nightMode, onlineState } =
    useContext(UserContext);
  const {
    UPDATE_INCIDENT_TICKET,
    REMOVE_INCIDENT_TICKET,
    ADD_TICKET_TO_INCIDENT,
  } = INCIDENT_ACTIONS;
  const { UPDATE_TICKET } = TICKET_ACTIONS;
  const [ticketViolations, setTicketViolations] = useState([]);
  const [municipalViolations, setMunicipalViolations] = useState([]);
  const [municipalWarnings, setMunicipalWarnings] = useState([]);
  const [ticketViolationWarnings, setTicketViolationWarnings] = useState([]);
  const [commercialViolationContainers, setCommercialViolationContainers] =
    useState([]);

  const [personAddresses, setPersonAddresses] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [localWarningValue, setLocalWarningValue] = useState(() =>
    ticket?.ticketViolationPrototype?.name
      ? isTicketWarning(ticket?.ticketViolationPrototype)
      : false,
  );
  const weightPenaltyContext = useMemo(
    () => ({
      isWeightViolation: displayCommercialFields(
        ticket?.ticketViolationPrototype?.ticketCategory?.name,
      ),
      weightLimit: ticket?.weightLimit,
      violationOccurrence: ticket?.violationOccurrence,
      violationName: ticket?.ticketViolationPrototype?.name,
    }),
    [
      ticket?.ticketViolationPrototype?.ticketCategory?.name,
      ticket?.ticketViolationPrototype?.name,
      ticket?.weightLimit,
      ticket?.violationOccurrence,
    ],
  );
  const {
    highways,
    municipalities,
    ticketDeliveryTypes,
    federalViolationCodes,
    reliefActTypes,
    deliveredToTypes,
    weatherConditions,
    roadConditions,
    trafficConditions,
    lightingConditions,
    weightLimits,
    violationOccurrences,
    states,
  } = setup.setupLists;

  const localStoreSpecificSetup =
    localGetData(`specific-setup-${incidentOriId}`) || {};
  const { setupLists: specificSetupLists } = localStoreSpecificSetup || {};
  const { areas, ticketStatuses } = specificSetupLists || {};
  const juvenile = useMemo(
    () =>
      isJuvenile(ticket?.issuedTo?.personalAttribute?.dob, ticket?.issuedAt),
    [ticket?.issuedTo?.personalAttribute?.dob, ticket?.issuedAt],
  );
  const {
    generateTicketNumber,
    updateTicketAccident,
    updateTicketActualSpeed,
    updateTicketCdl,
    updateTicketCode,
    updateTicketCommercialVehicle,
    updateTicketCurrentStatus,
    updateTicketCvHazardFee,
    updateTicketDefendantActions,
    updateTicketDefendantParent,
    updateTicketDeliveredTo,
    updateTicketDispositionPenaltyAmount,
    updateTicketFatality,
    updateTicketFederalViolationCode,
    updateTicketHazmat,
    updateTicketHighway,
    updateTicketIssuedAt,
    updateTicketIssuedTo,
    updateTicketMailingAddressVerified,
    updateTicketMunicipalOrdinance,
    updateTicketMunicipality,
    updateTicketNarrative,
    updateTicketOfficer,
    updateTicketOperator,
    updateTicketPassengerTicketEvidence,
    updateTicketPassengerTicketOutcome,
    updateTicketPassengerTicketSearch,
    updateTicketPerceivedRace,
    updateTicketPostedLimit,
    updateTicketRelativeCrimeCharge,
    updateTicketReliefActComment,
    updateTicketReliefActType,
    updateTicketSeatBeltOccurrence,
    updateTicketDeliveryType,
    updateTicketEvidence,
    updateTicketLocation,
    updateTicketNumber,
    updateTicketOutcome,
    updateTicketSearch,
    updateTicketStatus,
    updateTicketStop,
    updateTicketTrespassRequestor,
    updateTicketTrespassUntil,
    updateTicketUofInjuryDetails,
    updateTicketUofInstance,
    updateTicketUofInstanceTypes,
    updateTicketVehicle,
    updateTicketViolationDate,
    updateTicketAllFields,
    updateTicketViolationType,
    updateTicketWeightLimit,
    updateTicketViolationOccurrence,
    updateTicketConditionAllFields,
    updateSpeedMeasurement,
  } = useContext(MutationContext);
  const [submitCitation] = useMutation(SUBMIT_CITATION, {
    context: { serializationKey: "incidentQueue" },
  });
  const componentRef = useRef();
  const printStylesRef = useRef("");

  const [showPrintModal, setShowPrintModal] = useState(false);

  const [validating, setValidating] = useState(false);
  const [numberValidating, setNumberValidating] = useState(false);
  const [validationTest, setValidationTest] = useState({
    violationLocation: true,
    violationLocationIsComplete: true,
    municipality: true,
    issuedTo: true,
    validDob: true,
    validLastName: true,
    mailingAddress: true,
    mailingAddressIsComplete: true,
    validMailingAddress: true,
    defendantDidThen: true,
    reliefActType: true,
    under17ReliefActType: true,
    under17OnlyReliefActType: true,
    organizationReliefActType: true,
    individualReliefActType: true,
    deliveredTo: true,
    violationSelected: true,
    validLicenseNumber: true,
    licenseStateSet: true,
    actualSpeedAbovePosted: true,
    actualSpeedPresent: true,
    postedSpeedPresent: true,
    violationDateInPast: true,
    officerStateIdIsPresent: true,
    officerStateIdIsValid: true,
    violationMatchesOutcome: true,
    warningOutcomeIsWarning: true,
    parentAddressPresentOrNotRequired: true,
    parentAddressCompleteOrNotRequired: true,
    ticketNumberIsValid: true,
    deliveredBy: true,
    dateServed: true,
    dateServedIsAfterVioDate: true,
  });

  const state = useMemo(() => {
    return (
      states.find(
        state =>
          state.name === (ticket?.state?.name || ticket?.licenseStateName),
      ) || null
    );
  }, [states, ticket?.licenseStateName, ticket?.state?.name]);

  const [addAuditLog] = useMutation(ADD_AUDIT_LOG, {
    context: { serializationKey: "incidentQueue" },
  });
  const [setLastContacted] = useMutation(SET_LAST_CONTACTED, {
    context: { serializationKey: "incidentQueue" },
  });

  const addPrintToAuditLog = () => {
    const isWarning = isTicketWarning(ticket?.ticketViolationPrototype);
    addAuditLog({
      variables: {
        input: {
          originatorId: user?.id,
          action: "Print",
          message: `Printed ${isWarning ? "warning" : "ticket"}: ${
            ticket?.ticketNumber
          }`,
          incidentId: incidentId ? incidentId : incidentUuid,
        },
      },
    });
  };

  const preparePrintStyles = () => {
    const requiredClassName = isTicketWarning(ticket?.ticketViolationPrototype)
      ? warningFormStyles.warningVerticalGrid
      : ticketFormStyles.verticalGrid;
    printStylesRef.current = `${pageStyle(localWarningValue)}\n${getLoadedPrintStyles(requiredClassName)}`;
  };

  const handlePrintTicket = () => {
    const serverTimeFormat = moment.utc().format("YYYY-MM-DD HH:mm:ss UTC");
    setLastContacted({
      variables: {
        input: {
          officerId: user?.id,
          lastContactedAt: serverTimeFormat,
        },
      },
    });
    addPrintToAuditLog();
    printTicket();
  };

  const printTicket = useReactToPrint({
    content: () => componentRef.current,
    copyStyles: false,
    pageStyle: () => printStylesRef.current,
  });
  const handleDispatch = useCallback(
    (inIncidentAction, outOfIncidentAction, payload) => {
      if (inIncident) {
        let nextPayload = payload;
        if (
          inIncidentAction === UPDATE_INCIDENT_TICKET ||
          inIncidentAction === REMOVE_INCIDENT_TICKET
        ) {
          const incidentIdValue = payload?.incidentId ?? incidentId;
          const incidentUuidValue = payload?.incidentUuid ?? incidentUuid;
          const ticketIdValue = payload?.ticketId ?? ticket?.id;
          const ticketUuidValue = payload?.ticketUuid ?? ticket?.uuid;
          if (
            (incidentIdValue == null && incidentUuidValue == null) ||
            (ticketIdValue == null && ticketUuidValue == null)
          ) {
            return null;
          }
          nextPayload = {
            ...payload,
            incidentId: incidentIdValue,
            incidentUuid: incidentUuidValue,
            ticketId: ticketIdValue,
            ticketUuid: ticketUuidValue,
          };
          if (inIncidentAction === UPDATE_INCIDENT_TICKET) {
            nextPayload = normalizeTicketUpdatePayload(nextPayload);
          }
        }
        incidentDispatch({ type: inIncidentAction, payload: nextPayload });
      } else {
        const nextPayload =
          outOfIncidentAction === UPDATE_TICKET
            ? normalizeTicketUpdatePayload(payload)
            : payload;
        ticketDispatch({ type: outOfIncidentAction, payload: nextPayload });
      }
      return null;
    },
    [
      inIncident,
      incidentDispatch,
      ticketDispatch,
      incidentId,
      incidentUuid,
      ticket?.id,
      ticket?.uuid,
      UPDATE_INCIDENT_TICKET,
      REMOVE_INCIDENT_TICKET,
      UPDATE_TICKET,
    ],
  );

  const setCurrentStatus = useSetCurrentStatus(
    ticket,
    incidentId ? incidentId : incidentUuid,
    user,
    handleDispatch,
    updateTicketCurrentStatus,
    onlineState,
  );
  const handleTicketSubmission = useHandleTicketSubmission(
    ticket,
    canSubmitToJb,
    user,
    isMunicipalVio,
    isSuperUser,
    submitCitation,
    setCurrentStatus,
    onlineState,
  );
  const handlePrint = useHandlePrint(
    ticket,
    handlePrintTicket,
    setCurrentStatus,
    handleTicketSubmission,
    onlineState,
    preparePrintStyles,
  );

  useEffect(() => {
    if (
      ticket?.ticketViolationPrototype?.ticketEntry &&
      ticket?.ticketViolationPrototype?.waiver &&
      (ticket?.currentStatus < 3 || !ticket?.currentStatus)
    ) {
      setLocalWarningValue(isTicketWarning(ticket?.ticketViolationPrototype));
    }
  }, [ticket?.currentStatus, ticket?.ticketViolationPrototype]);

  useEffect(() => {
    if (
      ticket?.ticketLocation?.id &&
      (ticket?.currentStatus < 3 || !ticket?.currentStatus)
    ) {
      setTicketLocation({
        addressName: ticket?.ticketLocation?.name
          ? ticket?.ticketLocation?.name
          : "",
        streetNumber: ticket?.ticketLocation?.streetNumber
          ? ticket?.ticketLocation?.streetNumber
          : "",
        streetName: ticket?.ticketLocation?.streetName
          ? ticket?.ticketLocation?.streetName
          : "",
        unit: ticket?.ticketLocation?.unit ? ticket?.ticketLocation?.unit : "",
        city: ticket?.ticketLocation?.city ? ticket?.ticketLocation?.city : "",
        state: ticket?.ticketLocation?.state
          ? ticket?.ticketLocation?.state
          : "",
        zip: ticket?.ticketLocation?.zip ? ticket?.ticketLocation?.zip : "",
        id: ticket?.ticketLocation?.id ? ticket?.ticketLocation?.id : "",
      });
    }
  }, [ticket?.currentStatus, ticket?.ticketLocation]);

  const inputLabel = useRef(null);
  const [labelWidth, setLabelWidth] = useState(0);

  useEffect(() => {
    if (inputLabel.current) {
      setLabelWidth(inputLabel.current.offsetWidth);
    }
  }, [inputLabel]);

  useEffect(() => {
    //if ticket doesn't have a state but the ticket has been issued to someone
    if (
      Object.prototype.hasOwnProperty.call(ticket, "state") &&
      !ticket?.state?.name &&
      ticket?.issuedTo &&
      (ticket?.currentStatus < 3 || !ticket?.currentStatus)
    ) {
      //find that person
      const person = incidents[incidentIndex]?.personIncidentSpecifics?.find(
        pis => pis?.person?.id === ticket?.issuedTo?.id,
      )?.person;
      //find their licenseState
      const personLicenseState =
        person?.personalAttribute?.licenseState || null;
      //if the license state exists, then update the state and the server
      if (
        person &&
        personLicenseState &&
        personLicenseState?.id !== ticket?.state?.id
      ) {
        const mockEvent = {
          target: { name: "licenseState", value: personLicenseState },
        };
        // console.log('licenseState useEffect: ', mockEvent)
        handleChange(mockEvent, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket]);

  useEffect(() => {
    //if ticket doesn't have a license number but the ticket has been issued to someone
    if (
      !ticket?.driversLicense &&
      ticket?.issuedTo &&
      (ticket?.currentStatus < 3 || !ticket?.currentStatus)
    ) {
      //find that person
      const person = incidents[incidentIndex]?.personIncidentSpecifics?.find(
        pis => pis?.person?.id === ticket?.issuedTo?.id,
      )?.person;
      //find their driver's license number
      const personLicenseNumber =
        person?.personalAttribute?.driversLicense || "";
      //if the license number exists, then update the ticket state and the server data
      if (person && personLicenseNumber !== ticket?.driversLicense) {
        const mockEvent = {
          target: { name: "licenseNumber", value: personLicenseNumber },
        };
        // console.log('licenseNumber useEffect: ', mockEvent)
        handleChange(mockEvent, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket]);

  useEffect(() => {
    if (ticket?.currentStatus < 3 || !ticket?.currentStatus) {
      const person = incidents[incidentIndex]?.personIncidentSpecifics?.find(
        pis => pis?.person?.id === ticket?.issuedTo?.id,
      )?.person;
      const personLicenseNumber =
        person?.personalAttribute?.driversLicense || "";
      const personLicenseState =
        person?.personalAttribute?.licenseState || null;
      if (person && personLicenseNumber !== ticket?.driversLicense) {
        const mockEvent = {
          target: { name: "licenseNumber", value: personLicenseNumber },
        };
        // console.log('licenseNumber useEffect #2: ', mockEvent)
        handleChange(mockEvent, true);
      }
      if (
        Object.prototype.hasOwnProperty.call(ticket, "state") &&
        person &&
        personLicenseState &&
        personLicenseState?.id !== ticket?.state?.id
      ) {
        const mockEvent = {
          target: { name: "licenseState", value: personLicenseState },
        };
        // console.log('licenseState useEffect #2: ', mockEvent)
        handleChange(mockEvent, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket]);

  const memoizedDisabled = useMemo(() => {
    if (memoizedLocked) {
      return true;
    } else if (ticket?.currentStatus > 2) {
      return true;
    } else {
      return false;
    }
  }, [memoizedLocked, ticket?.currentStatus]);

  const [removeTicketFromIncident] = useMutation(REMOVE_TICKET_FROM_INCIDENT, {
    context: { serializationKey: "incidentQueue" },
  });

  useEffect(() => {
    indexedDb.ticket_violations
      .toArray()
      .then(res => {
        if (!res || res.length === 0) {
          checkTables(["ticket_violations"], db, client);
          return;
        } else {
          setTicketViolations(res);
        }
      })
      .catch(err => console.error(err));
  }, [indexedDb.ticket_violations, client]);

  useEffect(() => {
    indexedDb.municipal_violations
      .toArray()
      .then(res => {
        if (!res || res.length === 0) {
          checkTables(["municipal_violations"], db, client);
        } else {
          const oriSpecificVios = res.filter(
            v => v?.oriId?.toString() === incidentOriId?.toString(),
          );
          const vios = oriSpecificVios.filter(v => !isTicketWarning(v));
          const warnings = oriSpecificVios.filter(v => isTicketWarning(v));
          setMunicipalViolations(vios);
          setMunicipalWarnings(warnings);
        }
      })
      .catch(err => console.error(err));
  }, [incidentOriId, indexedDb.municipal_violations, client]);

  useEffect(() => {
    indexedDb.ticket_violations_warnings
      .toArray()
      .then(res => {
        if (!res || res.length === 0) {
          checkTables(["ticket_violations_warnings"], db, client);
        } else {
          setTicketViolationWarnings(res);
        }
      })
      .catch(err => console.error(err));
  }, [indexedDb.ticket_violations_warnings, client]);

  useEffect(() => {
    indexedDb.commercial_violation_containers
      .toArray()
      .then(res => {
        if (!res || res.length === 0) {
          checkTables(["commercial_violation_containers"], db, client);
        } else {
          setCommercialViolationContainers(res);
        }
      })
      .catch(err => console.error(err));
  }, [indexedDb.commercial_violation_containers, client]);

  useEffect(() => {
    let issuedToPis = piss?.find(
      pis => pis?.person?.id === ticket?.issuedTo?.id,
    );
    let addressArr = [
      issuedToPis?.person?.personalAttribute?.homeAddress,
      issuedToPis?.person?.personalAttribute.mailingAddress,
    ];
    let filteredArr = addressArr.filter(a => !!a);
    if (
      ticket?.issuedToAddress &&
      !_.some(filteredArr, ["id", ticket?.issuedToAddress.id])
    ) {
      filteredArr.push(ticket?.issuedToAddress);
    }
    setPersonAddresses(filteredArr);
  }, [onlineState, piss, ticket?.issuedTo, ticket?.issuedToAddress]);

  useEffect(() => {
    let arr = [];
    if (viss && viss?.length > 0) {
      viss.map(vis => {
        let id = vis?.vehicle?.id;
        let make = vis?.vehicle?.vehicleMake
          ? vis?.vehicle?.vehicleMake?.name
          : "";
        let model = vis?.vehicle?.vehicleModel
          ? vis?.vehicle?.vehicleModel?.name
          : "";
        let plate = vis?.vehicle?.vehiclePlate
          ? vis?.vehicle?.vehiclePlate?.name
          : "";
        arr.push({ id: id, name: `${make} (${model}) - ${plate}` });
        return null;
      });
    }
    setVehicles(arr);
  }, [viss]);

  const [ticketLocation, setTicketLocation] = useState(null);
  const getOptionIdOrNull = value => value?.id ?? null;
  const getOptionNameOrNull = value => value?.name ?? null;

  useEffect(() => {
    setTicketLocation({
      addressName: ticket?.ticketLocation?.name
        ? ticket?.ticketLocation?.name
        : "",
      streetNumber: ticket?.ticketLocation?.streetNumber
        ? ticket?.ticketLocation?.streetNumber
        : "",
      streetName: ticket?.ticketLocation?.streetName
        ? ticket?.ticketLocation?.streetName
        : "",
      unit: ticket?.ticketLocation?.unit ? ticket?.ticketLocation?.unit : "",
      city: ticket?.ticketLocation?.city ? ticket?.ticketLocation?.city : "",
      state: ticket?.ticketLocation?.state ? ticket?.ticketLocation?.state : "",
      zip: ticket?.ticketLocation?.zip ? ticket?.ticketLocation?.zip : "",
      id: ticket?.ticketLocation?.id ? ticket?.ticketLocation?.id : "",
    });
  }, [ticket?.ticketLocation]);

  const handleTicketConditionChange = (event) => {
    const name = event.target.name || null;
    const value = event.target.value;
    const fieldToUpdate = stateToMutation[name];
    const payload = {
      incidentId: incidentId || incidentUuid,
      ticketId: ticket?.id || ticket?.uuid,
      accessor: "ticketCondition",
      property: name,
      value: value,
    };
    handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, payload);
    updateTicketConditionAllFields({
      variables: {
        input: {
          originatorId: user?.id,
          officerId: ticket?.officer?.id,
          ticketId: ticket.id ? ticket.id : ticket.uuid,
          ticketConditionId: ticket?.ticketCondition?.id,
          [fieldToUpdate]: parseObjectArrayIdString(value),
        },
      },
      context: { serializationKey: "incidentQueue" },
    }).then(response => {
      if (
        response.data.updateTicketCondition.ticketConditionUuid?.includes(
          "Offline_",
        ) &&
        inIncident
      ) {
        handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
          incidentId: incidentId || incidentUuid,
          ticketId: ticket?.id || ticket?.uuid,
          accessor: "ticketCondition",
          property: "id",
          value: response.data.updateTicketCondition.ticketCondition.id,
        });
      }
    });
  };

  //THIS FIRES WHEN ANOTHER USER MAKES A CHANGE TO MUNICIPALITY
  useEffect(() => {
    if (
      ticket?.actualSpeed &&
      ticket?.postedLimit &&
      ticket?.waiver &&
      ticket?.points &&
      ticket?.penaltyMin &&
      ticket?.penaltyMax
    ) {
      const penalty = calculatePenalty(
        ticket?.actualSpeed,
        ticket?.postedLimit,
        ticket?.waiver,
        ticket?.points,
        ticket?.penaltyMin,
        ticket?.penaltyMax,
        ticket?.cvHazardFee,
        ticket?.seatBeltOccurrence,
        localWarningValue,
        ticket?.ticketViolationPrototype?.speedingVio,
        weightPenaltyContext,
      );
      if (+ticket?.penalty !== +penalty) {
        if (ticket?.currentStatus < 3) {
          // console.log('ticket useEffect')
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: "penalty",
            value: penalty,
          });
          updateTicketAllFields({
            variables: {
              input: {
                incidentId: incidentId ? incidentId : incidentUuid,
                originatorId: user?.id,
                officerId: ticket?.officer?.id,
                ticketId: ticket.id ? ticket.id : ticket.uuid,
                penalty: penalty,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
          setCurrentStatus(1);
        } else {
          // alert('The calculated waiver amount may be different than the amount showing. Please contact your Program Manager for assistance.')
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ticket?.actualSpeed,
    ticket?.postedLimit,
    ticket?.waiver,
    ticket?.points,
    ticket?.penaltyMin,
    ticket?.penaltyMax,
    ticket?.cvHazardFee,
    ticket?.seatBeltOccurrence,
    localWarningValue,
    incidentIndex,
    ticket?.id,
    ticket?.ticketViolationPrototype?.speedingVio,
    weightPenaltyContext,
  ]);

  const handleChange = useCallback(
    (event, submit = true, updateStatus = true) => {
      let name;
      let value;
      let mutationValue;
      if (event.target.type === "checkbox") {
        value = event.target.checked;
        name = event.target.value;
        mutationValue = value;
        submit = true;
      } else {
        name = event.target.name || null;
        value = event.target.value;
        mutationValue = getValueFromEventValue(event.target.value);
      }
      const fieldToUpdate = stateToMutation[name];
      updateStatus && setCurrentStatus(1);

      if (!name) {
        alert(
          `Please add a name field that matches the state name to the component that was just updated`,
        );
        return null;
      }
      switch (name) {
        case "ticketNumber":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketNumber({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "federalViolationCode":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketFederalViolationCode({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "cvHazardFee": {
          const cvHazardFee = value ?? false;
          const cvPenalty = calculatePenalty(
            ticket?.actualSpeed,
            ticket?.postedLimit,
            ticket?.waiver,
            ticket?.points,
            ticket?.penaltyMin,
            ticket?.penaltyMax,
            cvHazardFee,
            ticket?.seatBeltOccurrence,
            localWarningValue,
            ticket?.ticketViolationPrototype?.speedingVio,
            weightPenaltyContext,
          );
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: "penalty",
            value: cvPenalty,
          });
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketCvHazardFee({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                  penalty: cvPenalty,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        }
        case "highway":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketHighway({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "municipality":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketMunicipality({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "ticketDeliveryType":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketDeliveryType({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "issuedTo": {
          //check if the person is a juvenile - if so, set the civ relief statement, otherwise clear the statement
          if (
            isUnder17OnDate(
              value?.personalAttribute?.dob,
              ticket?.violationDate,
            ) &&
            ticket?.reliefActType?.name !== UNDER_17_RELIEF_ACT_TYPE_NAME
          ) {
            const mockEvent = {
              target: {
                name: "reliefActType",
                value: reliefActTypes.find(
                  r => r?.name === UNDER_17_RELIEF_ACT_TYPE_NAME,
                ),
              },
            };
            handleChange(mockEvent, true);
          }
          const clearAddressEvent = {
            target: { name: "issuedToAddress", value: "" },
          };
          handleChange(clearAddressEvent, false);
          const clearAddressVerification = {
            target: { name: "mailingAddressVerified", value: false },
          };
          handleChange(clearAddressVerification, false);
          // else if (ticket?.reliefActType) {
          //   const mockEvent = { target: { name: "reliefActType", value: "" } }
          //   handleChange(mockEvent, true)
          // }
          const licenseNumber =
            incidents[incidentIndex]?.personIncidentSpecifics?.find(
              pis => pis?.person?.id === value.id,
            )?.person?.personalAttribute?.driversLicense || "";
          const licenseState =
            incidents[incidentIndex]?.personIncidentSpecifics?.find(
              pis => pis?.person?.id === value.id,
            )?.person?.personalAttribute?.licenseState || "";
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: "driversLicense",
            value: licenseNumber,
          });
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: "state",
            value: licenseState,
          });
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketIssuedTo({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                  driversLicense: licenseNumber,
                  stateId: licenseState?.id || "",
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        }

        //These two are for useEffect updates as the fields cannot be changed in this frame
        case "licenseState":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: "state",
            value: value,
          });
          submit &&
            updateTicketIssuedTo({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "licenseNumber":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: "driversLicense",
            value: value,
          });
          submit &&
            updateTicketIssuedTo({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue || "",
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "issuedToAddress":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketIssuedAt({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "mailingAddressVerified":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketMailingAddressVerified({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        // case "isWarning":
        //   handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, { incidentId: incidentId || incidentUuid, ticketId: ticket?.id || ticket?.uuid, property: name, value: value })
        //   submit && updateTicketIsWarning({ variables: { input: { incidentId: incidentId ? incidentId : incidentUuid, originatorId: user?.id, officerId: ticket?.officer?.id, ticketId: ticket.id ? ticket.id : ticket.uuid, [fieldToUpdate]: value } }, context: { serializationKey: 'incidentQueue' } })
        //   updateLocalTicketViolation("", "ticketViolationPrototype")
        //   return null;
        case "officer":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketOfficer({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "trespassRequestor":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketTrespassRequestor({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "code":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketCode({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "actualSpeed": {
          const speedPenalty = calculatePenalty(
            value,
            ticket?.postedLimit,
            ticket?.waiver,
            ticket?.points,
            ticket?.penaltyMin,
            ticket?.penaltyMax,
            ticket?.cvHazardFee,
            ticket?.seatBeltOccurrence,
            localWarningValue,
            ticket?.ticketViolationPrototype?.speedingVio,
            weightPenaltyContext,
          );
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: "penalty",
            value: speedPenalty,
          });
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: +value,
          });
          submit &&
            updateTicketActualSpeed({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: +value,
                  penalty: speedPenalty,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        }
        case "postedLimit": {
          const postedPenalty = calculatePenalty(
            ticket?.actualSpeed,
            value,
            ticket?.waiver,
            ticket?.points,
            ticket?.penaltyMin,
            ticket?.penaltyMax,
            ticket?.cvHazardFee,
            ticket?.seatBeltOccurrence,
            localWarningValue,
            ticket?.ticketViolationPrototype?.speedingVio,
            weightPenaltyContext,
          );
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: "penalty",
            value: postedPenalty,
          });
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: +value,
          });
          submit &&
            updateTicketPostedLimit({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: +value,
                  penalty: postedPenalty,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        }
        case "ticketStatus":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketStatus({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "dispositionPenaltyAmount":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketDispositionPenaltyAmount({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "defendantActions":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketDefendantActions({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "cdl":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketCdl({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "reliefActType":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketReliefActType({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "reliefActComment":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketReliefActComment({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "defendantParent":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketDefendantParent({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "deliveredToType":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketDeliveredTo({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "municipalOrdinance":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketMunicipalOrdinance({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "operator":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketOperator({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "vehicle":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value:
              viss.find(vis => vis?.vehicle?.id === value?.id)?.vehicle || null,
          });
          submit &&
            updateTicketVehicle({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        //race data fields
        case "perceivedRace":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketPerceivedRace({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "ticketStopType":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketStop({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "ticketSearchType":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketSearch({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "ticketEvidenceType":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketEvidence({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "ticketOutcomeType":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketOutcome({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "passengerTicketSearchType":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketPassengerTicketSearch({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "passengerTicketEvidenceType":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketPassengerTicketEvidence({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "passengerTicketOutcomeType":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketPassengerTicketOutcome({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        //end race fields
        case "seatBeltOccurrence": {
          const calculatedPenalty = calculatePenalty(
            ticket?.actualSpeed,
            ticket?.postedLimit,
            ticket?.waiver,
            ticket?.points,
            ticket?.penaltyMin,
            ticket?.penaltyMax,
            ticket?.cvHazardFee,
            value?.name,
            localWarningValue,
            ticket?.ticketViolationPrototype?.speedingVio,
            weightPenaltyContext,
          );
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: "penalty",
            value: calculatedPenalty,
          });
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: getOptionNameOrNull(value),
          });
          submit &&
            updateTicketSeatBeltOccurrence({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                  penalty: calculatedPenalty,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        }
        case "accident":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketAccident({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "hazmat":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketHazmat({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "fatality":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketFatality({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "commercialVehicle":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketCommercialVehicle({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "relativeCrimeCharge":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketRelativeCrimeCharge({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "narrative":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketNarrative({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "weightLimit":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketWeightLimit({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "violationOccurrence":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketViolationOccurrence({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "radarIdStr":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: value,
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  radarIdStr: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "radarTuningForksBefore":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  radarTuningForksBefore: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "radarInternalCalibrationBefore":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  radarInternalCalibrationBefore: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "radarSpeedometerVerifBefore":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  radarSpeedometerVerifBefore: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "radarDisplayLightsBefore":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  radarDisplayLightsBefore: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "radarTuningForksAfter":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  radarTuningForksAfter: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "radarInternalCalibrationAfter":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  radarInternalCalibrationAfter: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "radarSpeedometerVerifAfter":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  radarSpeedometerVerifAfter: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "radarDisplayLightsAfter":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  radarDisplayLightsAfter: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "laserIdStr":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: value,
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  laserIdStr: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "laserSelfBefore":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  laserSelfBefore: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "laserScopeAlignmentBefore":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  laserScopeAlignmentBefore: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "laserKnownDistanceBefore":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  laserKnownDistanceBefore: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "laserDisplayLightsBefore":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  laserDisplayLightsBefore: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "laserSelfAfter":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  laserSelfAfter: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "laserScopeAlignmentAfter":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  laserScopeAlignmentAfter: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "laserKnownDistanceAfter":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  laserKnownDistanceAfter: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "laserDisplayLightsAfter":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            accessor: "speedMeasurement",
            property: name,
            value: getOptionIdOrNull(value),
          });
          submit &&
            updateSpeedMeasurement({
              variables: {
                input: {
                  ticketId: ticket?.id,
                  speedMeasurementId: ticket?.speedMeasurement?.id,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  laserDisplayLightsAfter: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        case "uofInstance":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketUofInstance({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: mutationValue,
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "uofInstanceTypes":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketUofInstanceTypes({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: parseObjectArrayIdString(value),
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;
        case "uofInjuryDetails":
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId || incidentUuid,
            ticketId: ticket?.id || ticket?.uuid,
            property: name,
            value: value,
          });
          submit &&
            updateTicketUofInjuryDetails({
              variables: {
                input: {
                  incidentId: incidentId ? incidentId : incidentUuid,
                  originatorId: user?.id,
                  officerId: ticket?.officer?.id,
                  ticketId: ticket.id ? ticket.id : ticket.uuid,
                  [fieldToUpdate]: parseObjectArrayIdString(value),
                },
              },
              context: { serializationKey: "incidentQueue" },
            });
          return null;

        default:
          alert(
            `Name not found in handle function in TicketAndWarning.js: ${name}`,
          );
          return null;
      }
    },
    [
      UPDATE_INCIDENT_TICKET,
      UPDATE_TICKET,
      handleDispatch,
      incidentId,
      incidentIndex,
      incidentUuid,
      incidents,
      localWarningValue,
      reliefActTypes,
      setCurrentStatus,
      ticket?.actualSpeed,
      weightPenaltyContext,
      ticket?.cvHazardFee,
      ticket.id,
      ticket?.violationDate,
      ticket?.officer?.id,
      ticket?.penaltyMax,
      ticket?.penaltyMin,
      ticket?.points,
      ticket?.postedLimit,
      ticket?.reliefActType?.name,
      ticket?.seatBeltOccurrence,
      ticket?.speedMeasurement?.id,
      ticket?.ticketViolationPrototype?.speedingVio,
      ticket.uuid,
      ticket?.waiver,
      updateSpeedMeasurement,
      updateTicketAccident,
      updateTicketActualSpeed,
      updateTicketCdl,
      updateTicketCode,
      updateTicketCommercialVehicle,
      updateTicketCvHazardFee,
      updateTicketDefendantActions,
      updateTicketDefendantParent,
      updateTicketDeliveredTo,
      updateTicketDeliveryType,
      updateTicketDispositionPenaltyAmount,
      updateTicketEvidence,
      updateTicketFatality,
      updateTicketFederalViolationCode,
      updateTicketHazmat,
      updateTicketHighway,
      updateTicketIssuedAt,
      updateTicketIssuedTo,
      updateTicketMailingAddressVerified,
      updateTicketMunicipalOrdinance,
      updateTicketMunicipality,
      updateTicketNarrative,
      updateTicketNumber,
      updateTicketOfficer,
      updateTicketOperator,
      updateTicketOutcome,
      updateTicketPassengerTicketEvidence,
      updateTicketPassengerTicketOutcome,
      updateTicketPassengerTicketSearch,
      updateTicketPerceivedRace,
      updateTicketPostedLimit,
      updateTicketRelativeCrimeCharge,
      updateTicketReliefActComment,
      updateTicketReliefActType,
      updateTicketSearch,
      updateTicketSeatBeltOccurrence,
      updateTicketStatus,
      updateTicketStop,
      updateTicketTrespassRequestor,
      updateTicketUofInjuryDetails,
      updateTicketUofInstance,
      updateTicketUofInstanceTypes,
      updateTicketVehicle,
      updateTicketViolationOccurrence,
      updateTicketWeightLimit,
      user?.id,
      viss,
    ],
  );

  const updateLocalTicketViolation = (value, name, isWarningOverride) => {
    setCurrentStatus(1);
    const selectedIsWarning =
      isWarningOverride ?? (value ? isTicketWarning(value) : localWarningValue);
    const selectedWeightPenaltyContext = {
      isWeightViolation: displayCommercialFields(value?.ticketCategory?.name),
      weightLimit: ticket?.weightLimit,
      violationOccurrence: ticket?.violationOccurrence,
      violationName: value?.name,
    };
    const penalty = calculatePenalty(
      ticket?.actualSpeed,
      ticket?.postedLimit,
      +value?.waiver,
      +value?.points,
      +value?.minPenalty,
      +value?.maxPenalty,
      ticket?.cvHazardFee,
      ticket?.seatBeltOccurrence,
      selectedIsWarning,
      value?.speedingVio,
      selectedWeightPenaltyContext,
    );
    handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
      incidentId: incidentId || incidentUuid,
      ticketId: ticket?.id || ticket?.uuid,
      property: name,
      value: value,
    });
    handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
      incidentId: incidentId || incidentUuid,
      ticketId: ticket?.id || ticket?.uuid,
      property: "penaltyMax",
      value: value?.maxPenalty ? +value.maxPenalty : 0,
    });
    handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
      incidentId: incidentId || incidentUuid,
      ticketId: ticket?.id || ticket?.uuid,
      property: "penaltyMin",
      value: value?.minPenalty ? +value.minPenalty : 0,
    });
    handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
      incidentId: incidentId || incidentUuid,
      ticketId: ticket?.id || ticket?.uuid,
      property: "waiver",
      value: value?.waiver ? +value.waiver : 0,
    });
    handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
      incidentId: incidentId || incidentUuid,
      ticketId: ticket?.id || ticket?.uuid,
      property: "points",
      value: value?.points ? +value.points : 0,
    });
    handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
      incidentId: incidentId || incidentUuid,
      ticketId: ticket?.id || ticket?.uuid,
      property: "code",
      value: value?.code || "",
    });
    handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
      incidentId: incidentId || incidentUuid,
      ticketId: ticket?.id || ticket?.uuid,
      property: "weightLimit",
      value: null,
    });
    handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
      incidentId: incidentId || incidentUuid,
      ticketId: ticket?.id || ticket?.uuid,
      property: "violationOccurrence",
      value: null,
    });
    handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
      incidentId: incidentId || incidentUuid,
      ticketId: ticket?.id || ticket?.uuid,
      property: "penalty",
      value: penalty || "",
    });
    updateTicketViolationType({
      variables: {
        input: {
          incidentId: incidentId ? incidentId : incidentUuid,
          originatorId: user?.id,
          officerId: ticket?.officer?.id,
          ticketId: ticket.id ? ticket.id : ticket.uuid,
          ticketViolationPrototypeId: value?.id ? value.id : "",
          code: value?.code,
          penaltyMax: +value?.maxPenalty,
          penaltyMin: +value?.minPenalty,
          waiver: +value?.waiver,
          points: +value?.points,
          penalty: penalty,
          weightLimitId: "",
          violationOccurrenceId: "",
        },
      },
      context: { serializationKey: "incidentQueue" },
    });
  };

  const updateAddress = (address, updateStatus = true, addressName) => {
    const fieldToUpdate = stateToMutation[addressName];
    updateStatus && setCurrentStatus(1);
    switch (addressName) {
      case "ticketLocation":
        handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
          incidentId: incidentId || incidentUuid,
          ticketId: ticket?.id || ticket?.uuid,
          property: addressName,
          value: address ? address : null,
        });
        updateTicketLocation({
          variables: {
            input: {
              incidentId: incidentId ? incidentId : incidentUuid,
              originatorId: user?.id,
              officerId: ticket?.officer?.id,
              ticketId: ticket.id ? ticket.id : ticket.uuid,
              [fieldToUpdate]: address ? address?.id : "",
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        setTicketLocation({
          addressName: address?.name ? address?.name : "",
          streetNumber: address?.streetNumber ? address?.streetNumber : "",
          streetName: address?.streetName ? address?.streetName : "",
          unit: address?.unit ? address?.unit : "",
          city: address?.city ? address?.city : "",
          state: address?.state ? address?.state : "",
          zip: address?.zip ? address?.zip : "",
          id: address?.id ? address?.id : "",
        });
        return null;

      default:
        alert("Address name not found in lookup");
        return null;
    }
  };

  const handleDateChange = (value, name, date, updateStatus = true) => {
    const fieldToUpdate = stateToMutation[name];
    //blank time equals null value and null date
    updateStatus && setCurrentStatus(1);
    let serverTimeFormat = "";
    if (!date) {
      if (
        name === "radarTimeBefore" ||
        name === "radarTimeAfter" ||
        name === "laserTimeBefore" ||
        name === "laserTimeAfter"
      ) {
        handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
          incidentId: incidentId || incidentUuid,
          ticketId: ticket?.id || ticket?.uuid,
          accessor: "speedMeasurement",
          property: name,
          value: serverTimeFormat,
        });
        updateSpeedMeasurement({
          variables: {
            input: {
              ticketId: ticket?.id,
              speedMeasurementId: ticket?.speedMeasurement?.id,
              originatorId: user?.id,
              officerId: ticket?.officer?.id,
              [fieldToUpdate]: serverTimeFormat,
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        return;
      } else {
        handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
          incidentId: incidentId || incidentUuid,
          ticketId: ticket?.id || ticket?.uuid,
          property: name,
          value: serverTimeFormat,
        });
        // return;
      }
    } else if (date.isValid()) {
      serverTimeFormat = date.utc().format("YYYY-MM-DD HH:mm:ss UTC");
      if (
        name === "radarTimeBefore" ||
        name === "radarTimeAfter" ||
        name === "laserTimeBefore" ||
        name === "laserTimeAfter"
      ) {
        handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
          incidentId: incidentId || incidentUuid,
          ticketId: ticket?.id || ticket?.uuid,
          accessor: "speedMeasurement",
          property: name,
          value: serverTimeFormat,
        });
        updateSpeedMeasurement({
          variables: {
            input: {
              ticketId: ticket?.id,
              speedMeasurementId: ticket?.speedMeasurement?.id,
              originatorId: user?.id,
              officerId: ticket?.officer?.id,
              [fieldToUpdate]: serverTimeFormat,
            },
          },
          context: { serializationKey: "incidentQueue" },
        });
        return;
      } else {
        handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
          incidentId: incidentId || incidentUuid,
          ticketId: ticket?.id || ticket?.uuid,
          property: name,
          value: serverTimeFormat,
        });
        // return;
      }
      switch (name) {
        case "violationDate":
          updateTicketViolationDate({
            variables: {
              input: {
                incidentId: incidentId ? incidentId : incidentUuid,
                originatorId: user?.id,
                officerId: ticket?.officer?.id,
                ticketId: ticket.id ? ticket.id : ticket.uuid,
                [fieldToUpdate]: serverTimeFormat,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
          return null;
        case "issuedAt":
          updateTicketIssuedAt({
            variables: {
              input: {
                incidentId: incidentId ? incidentId : incidentUuid,
                originatorId: user?.id,
                officerId: ticket?.officer?.id,
                ticketId: ticket.id ? ticket.id : ticket.uuid,
                [fieldToUpdate]: serverTimeFormat,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
          return null;
        case "trespassUntil":
          updateTicketTrespassUntil({
            variables: {
              input: {
                incidentId: incidentId ? incidentId : incidentUuid,
                originatorId: user?.id,
                officerId: ticket?.officer?.id,
                ticketId: ticket.id ? ticket.id : ticket.uuid,
                [fieldToUpdate]: serverTimeFormat,
              },
            },
            context: { serializationKey: "incidentQueue" },
          });
          return null;

        default:
          alert("Name not found in handleDateChange lookup");
          return null;
      }
    }
  };

  const handleAutoCompleteChange = (value, name) => {
    setCurrentStatus(1);
    const fieldToUpdate = stateToMutation[name];
    handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
      incidentId: incidentId || incidentUuid,
      ticketId: ticket?.id || ticket?.uuid,
      property: name,
      value: value,
    });

    const mutation =
      name === "municipality"
        ? updateTicketMunicipality
        : updateTicketFederalViolationCode;

    mutation({
      variables: {
        input: {
          incidentId: incidentId ? incidentId : incidentUuid,
          originatorId: user?.id,
          officerId: ticket?.officer?.id,
          ticketId: ticket.id ? ticket.id : ticket.uuid,
          [fieldToUpdate]: value?.id || "",
        },
      },
      context: { serializationKey: "incidentQueue" },
    });
  };

  const handleRemoveTicket = (ticketId, ticketUuid) => {
    if (window.confirm(`Are you sure you want to remove this ticket?`)) {
      if (ticket?.currentStatus >= 3 && !isSuperUser(user?.roleNames)) {
        alert(
          `This ticket has already been ${
            ticket?.currentStatus === 4 ? "submitted" : "printed"
          }.`,
        );
        return null;
      } else {
        const incidentIdValue = incidentId ?? incidentUuid;
        const ticketIdValue = ticketId ?? ticketUuid;
        if (!incidentIdValue || !ticketIdValue) {
          alert("Incident ID or Ticket ID is missing.");
          return null;
        }
        removeTicketFromIncident({
          variables: {
            input: { ticketId: ticketIdValue, originatorId: user?.id },
          },
          context: { serializationKey: "incidentQueue" },
        });
        incidentDispatch({
          type: REMOVE_INCIDENT_TICKET,
          payload: {
            incidentId: incidentId ?? null,
            incidentUuid: incidentUuid ?? null,
            ticketId: ticketId ?? null,
            ticketUuid: ticketUuid ?? null,
          },
        });
        ticketsLength > 1 && setTicketTab(ticketTab > 0 ? ticketTab - 1 : 0);
        ticketsLength <= 1 && handleAddTicketToIncident(false, true, true);
        return null;
      }
    }
  };

  const handleAddTicketToIncident = _.throttle(
    (duplicate = false, setToFirstTab = false, forceCreation) => {
      if (hasBlankTicket && !forceCreation) {
        alert(
          "There is already a ticket without a violation in this incident. Please fill out each ticket before adding a new one.",
        );
        return null;
      }
      if (!onlineState && !user?.startingTicketNumber) {
        alert(
          "You are currently offline and out of offline ticket numbers. Please reconnect or use a paper ticket.",
        );
      } else {
        disableTicketAddition();
        let localTicket = createEmptyTicket(
          incidents[incidentIndex]?.callTime,
          incidents[incidentIndex]?.address,
          incidents[incidentIndex],
        );

        let input = {
          incidentId: incidentId || incidentUuid,
          originatorId: user?.id,
          ticketId: localTicket.id,
          issuedAt: incidents[incidentIndex]?.callTime
            ? incidents[incidentIndex]?.callTime
            : null,
          violationDate: incidents[incidentIndex]?.callTime
            ? incidents[incidentIndex]?.callTime
            : null,
          ticketLocationId: incidents[incidentIndex]?.address
            ? incidents[incidentIndex]?.address.id
            : null,
        };
        if (duplicate) {
          //set the duplicated fields both in state on localTicket and on input for the mutation
          localTicket.highway = ticket?.highway || null;
          input.highwayId = ticket?.highway?.id || null;

          localTicket.ticketLocation = ticket?.ticketLocation || null;
          input.ticketLocationId = ticket?.ticketLocation?.id || null;

          localTicket.municipality = ticket?.municipality || null;
          input.municipalityId = ticket?.municipality?.id || null;

          localTicket.violationDate = ticket?.violationDate || null;
          input.violationDate = ticket?.violationDate || null;

          localTicket.ticketDeliveryType = ticket?.ticketDeliveryType || null;
          input.ticketDeliveryTypeId = ticket?.ticketDeliveryType?.id || null;

          localTicket.issuedTo = ticket?.issuedTo || null;
          input.issuedToId = ticket?.issuedTo?.id || null;

          localTicket.issuedToAddress = ticket?.issuedToAddress || null;
          input.issuedToAddressId = ticket?.issuedToAddress?.id || null;

          localTicket.mailingAddressVerified =
            ticket?.mailingAddressVerified || null;
          input.mailingAddressVerified = ticket?.mailingAddressVerified || null;

          localTicket.issuedAt = ticket?.issuedAt || null;
          input.issuedAt = ticket?.issuedAt || null;

          localTicket.officer = ticket?.officer || null;
          input.officerId = ticket?.officer?.id || null;

          localTicket.reliefActType = ticket?.reliefActType || null;
          input.reliefActTypeId = ticket?.reliefActType?.id || null;

          localTicket.reliefActComment = ticket?.reliefActComment || null;
          input.reliefActComment = ticket?.reliefActComment || null;

          localTicket.deliveredToType = ticket?.deliveredToType || null;
          input.deliveredToTypeId = ticket?.deliveredToType?.id || null;

          localTicket.cdl = ticket?.cdl || false;
          input.cdl = ticket?.cdl || false;

          localTicket.vehicle = ticket?.vehicle || null;
          input.vehicleId = ticket?.vehicle?.id || null;

          localTicket.hazmat = ticket?.hazmat || false;
          input.hazmat = ticket?.hazmat || false;

          localTicket.fatality = ticket?.fatality || false;
          input.fatality = ticket?.fatality || false;

          localTicket.commercialVehicle = ticket?.commercialVehicle || false;
          input.commercialVehicle = ticket?.commercialVehicle || false;

          localTicket.relativeCrimeCharge =
            ticket?.relativeCrimeCharge || false;
          input.relativeCrimeCharge = ticket?.relativeCrimeCharge || false;

          localTicket.driversLicense = ticket?.driversLicense || null;
          input.driversLicense = ticket?.driversLicense || null;

          localTicket.state = ticket?.state || null;
          input.stateId = ticket?.state?.id || null;
        }
        incidentDispatch({
          type: ADD_TICKET_TO_INCIDENT,
          payload: {
            incidentId: incidentId ?? null,
            incidentUuid: incidentUuid ?? null,
            ticket: localTicket,
          },
        });
        updateTicketAllFields({
          variables: { input: input },
          context: { serializationKey: "incidentQueue" },
        }).then(res => {
          handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
            incidentId: incidentId ?? null,
            incidentUuid: incidentUuid ?? null,
            ticketId: res?.data?.addOrUpdateTicketInIncident?.ticket?.id,
            ticketUuid: res?.data?.addOrUpdateTicketInIncident?.ticketUuid,
            property: "id",
            value: res?.data?.addOrUpdateTicketInIncident?.ticket?.id,
          });
        });
        setTicketTab(setToFirstTab ? 0 : ticketsLength);
      }
    },
    1000,
    { leading: true, trailing: true },
  );

  const handleTicketNumberGeneration = ticketNumber => {
    //add mutation here to generate a ticket number
    if (ticketNumber) {
      //check the number
      if (isTicketWarning(ticket?.ticketViolationPrototype)) {
        if (/^[W]/.test(ticketNumber)) {
          //The warning number is good, open the print modal
          // openPrintTicketModal()
          return ticketNumber;
        } else {
          //The warning number is incorrect. Fire the mutation use a .then() to update the state with the number then open the modal
          generateTicketNumber({
            variables: {
              input: {
                officerId: user?.id,
                ticketId: ticket?.id ? ticket?.id : ticket?.uuid,
                civilVio: true,
              },
            },
          }).then(res => {
            const mockEvent = {
              target: {
                name: "ticketNumber",
                value: res.data.generateTicketNumber.ticketNumber,
              },
            };
            handleChange(mockEvent, true);
            const status = res.data.generateTicketNumber?.ticket?.currentStatus;
            openPrintTicketModal(
              res.data.generateTicketNumber.ticketNumber,
              status,
            );
            // return res.data.generateTicketNumber.ticketNumber
          });
        }
      } else {
        if (/^[7]\d{6}$/.test(ticketNumber)) {
          //The ticket number is good, open the print modal
          // openPrintTicketModal()
          return ticketNumber;
        } else {
          //The ticket number is incorrect. Fire the mutation use a .then() to update the state with the number then open the modal
          if (onlineState) {
            generateTicketNumber({
              variables: {
                input: {
                  officerId: user?.id,
                  ticketId: ticket?.id ? ticket?.id : ticket?.uuid,
                  civilVio: false,
                },
              },
            }).then(res => {
              const mockEvent = {
                target: {
                  name: "ticketNumber",
                  value: res.data.generateTicketNumber.ticketNumber,
                },
              };
              handleChange(mockEvent, true);
              const status =
                res.data.generateTicketNumber?.ticket?.currentStatus;
              openPrintTicketModal(
                res.data.generateTicketNumber.ticketNumber,
                status,
              );
              // return res.data.generateTicketNumber.ticketNumber
            });
          } else {
            //use offline number
            const offlineTicketNumber = generateOfflineTicketNumber(
              user?.startingTicketNumber,
              user?.endingTicketNumber,
            );
            if (!offlineTicketNumber) {
              alert(
                "Your offline ticket allocation does not contain any submittable ticket numbers.",
              );
              return;
            }
            const mockEvent = {
              target: {
                name: "ticketNumber",
                value: offlineTicketNumber.toString(),
              },
            };
            handleChange(mockEvent, true);
            incrementOfflineTicketNumber(offlineTicketNumber);
            // openPrintTicketModal(offlineTicketNumber)
            return offlineTicketNumber;
          }
        }
      }
    } else {
      if (onlineState) {
        //fire the mutation use a .then() to update the state with the number then open the modal
        generateTicketNumber({
          variables: {
            input: {
              officerId: user?.id,
              ticketId: ticket?.id ? ticket?.id : ticket?.uuid,
              civilVio: isTicketWarning(ticket?.ticketViolationPrototype),
            },
          },
        }).then(res => {
          const generatedTicketNumber =
            res?.data?.generateTicketNumber?.ticketNumber;
          const status = res?.data?.generateTicketNumber?.ticket?.currentStatus;

          if (!generatedTicketNumber) {
            console.error(
              "generateTicketNumber returned without a ticket number",
              res,
            );
            alert(
              "The ticket number could not be generated. Please refresh and try again.",
            );
            return;
          }

          const mockEvent = {
            target: {
              name: "ticketNumber",
              value: generatedTicketNumber,
            },
          };
          handleChange(mockEvent, true);
          openPrintTicketModal(generatedTicketNumber, status);
          // return res.data.generateTicketNumber.ticketNumber
        });
      } else if (!isTicketWarning(ticket?.ticketViolationPrototype)) {
        //use offline number
        const offlineTicketNumber = generateOfflineTicketNumber(
          user?.startingTicketNumber,
          user?.endingTicketNumber,
        );
        if (!offlineTicketNumber) {
          alert(
            "Your offline ticket allocation does not contain any submittable ticket numbers.",
          );
          return;
        }
        const mockEvent = {
          target: {
            name: "ticketNumber",
            value: offlineTicketNumber.toString(),
          },
        };
        handleChange(mockEvent, true);
        incrementOfflineTicketNumber(offlineTicketNumber);
        openPrintTicketModal(offlineTicketNumber);
        // return offlineTicketNumber
      } else {
        const now = moment().format("x").substring(0, 11);
        const offlineWarningNumber = "W".concat("", now);
        const mockEvent = {
          target: {
            name: "ticketNumber",
            value: offlineWarningNumber.toString(),
          },
        };
        handleChange(mockEvent, true);
        openPrintTicketModal(offlineWarningNumber);
      }
    }
  };

  const isTicketValid = async (ticket, localStatus) => {
    setValidating(true);
    let validationObj = returnTicketValidationObj(
      ticket,
      incidents,
      incidentIndex,
      ticketLocation,
      juvenile,
    );

    setValidationTest(validationObj);
    if (
      Object.keys(validationObj)
        .map(key => validationObj[key])
        .includes(false)
    ) {
      return false;
    } else {
      if (
        ticket?.currentStatus < 2 &&
        (localStatus !== undefined ? localStatus < 2 : true)
      ) {
        await setCurrentStatus(2);
      }
      setValidating(false);
      return true;
    }
  };

  const isNumberValid = (ticketNumber, isWarning) => {
    let obj = {};
    if (!isWarning && !canSubmitToJb) {
      //This is for a violation for a user without the eticket_user role. The ticket number needs to just not be blank
      obj = {
        ticketNumberIsValid: isWarning
          ? isWarningNumberValid(ticketNumber)
          : !!ticketNumber,
      };
    } else {
      obj = {
        ticketNumberIsValid: isWarning
          ? isWarningNumberValid(ticketNumber)
          : isTicketNumberValid(ticketNumber),
      };
    }
    const newValObj = _.merge(validationTest, obj);
    if (!obj.ticketNumberIsValid) {
      setNumberValidating(true);
      setValidationTest(newValObj);
    } else {
      setNumberValidating(false);
    }
    return obj.ticketNumberIsValid;
  };

  const openPrintTicketModal = async (localTicketNumber, localStatus) => {
    if (ticket?.currentStatus < 4 && !userRoles?.includes("Eticket_User")) {
      alert("Only E-Ticket Users can print unsubmitted E-Tickets");
      return;
    }
    const isWarning = isTicketWarning(ticket?.ticketViolationPrototype);

    if (
      ticket?.currentStatus > 2 &&
      isNumberValid(ticket?.ticketNumber, isWarning)
    ) {
      setShowPrintModal(true);
      return;
    }

    const isMuni = isMunicipalVio(ticket?.ticketViolationPrototype);
    if (isMuni && !isWarning) {
      alert(
        "Municipal civil violations may not be printed or submitted. If you meant to make this a municipal warning, please check the warning checkbox next to the ticket number and select the civil warning violation.",
      );
      return;
    }
    //if canSubmitToJb go on normally
    //if !canSubmitToJb && !isWarning => don't generate a ticket number, if blank throw validation error
    //if !canSubmitToJb $$ isWarning => go on normally
    if (canSubmitToJb || (!canSubmitToJb && isWarning)) {
      //go on normally
      if (isWarning || (await isTicketValid(ticket, localStatus))) {
        if (!ticket?.ticketNumber && !localTicketNumber) {
          handleTicketNumberGeneration();
          return;
        }
        if (
          isNumberValid(localTicketNumber || ticket?.ticketNumber, isWarning)
        ) {
          setShowPrintModal(true);
          return;
        } else {
          alert(
            "The ticket number is not an E-Ticket number and cannot be printed. If you meant to create an E-Ticket please delete the manually entered ticket number and click Print again.",
          );
          return;
        }
      } else {
        alert("Please fix the required elements");
        return;
      }
    } else {
      //don't generate a ticket number, just make sure the ticket number isn't blank
      if (isNumberValid(ticket?.ticketNumber, isWarning)) {
        setShowPrintModal(true);
        return;
      } else {
        alert(
          "You do not have sufficient roles to generate an E-Ticket number. ",
        );
      }
    }
  };

  const updateCvTicket = container => {
    if (
      +container?.maxPenalty !== +ticket?.penaltyMax ||
      +container?.minPenalty !== +ticket?.penaltyMin ||
      +container?.points !== +ticket?.points ||
      +container?.waiver !== ticket?.waiver
    ) {
      const calculatedPenalty = calculatePenalty(
        ticket?.actualSpeed,
        ticket?.postedLimit,
        +container?.waiver,
        +container?.points,
        +container?.minPenalty,
        +container?.maxPenalty,
        ticket?.cvHazardFee,
        ticket?.seatBeltOccurrence,
        localWarningValue,
        ticket?.ticketViolationPrototype?.speedingVio,
        weightPenaltyContext,
      );
      handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
        incidentId: incidentId || incidentUuid,
        ticketId: ticket?.id || ticket?.uuid,
        property: "penaltyMax",
        value: container?.maxPenalty ? +container.maxPenalty : 0,
      });
      handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
        incidentId: incidentId || incidentUuid,
        ticketId: ticket?.id || ticket?.uuid,
        property: "penaltyMin",
        value: container?.minPenalty ? +container.minPenalty : 0,
      });
      handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
        incidentId: incidentId || incidentUuid,
        ticketId: ticket?.id || ticket?.uuid,
        property: "waiver",
        value: container?.waiver ? +container.waiver : 0,
      });
      handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
        incidentId: incidentId || incidentUuid,
        ticketId: ticket?.id || ticket?.uuid,
        property: "points",
        value: container?.points ? +container.points : 0,
      });
      handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
        incidentId: incidentId || incidentUuid,
        ticketId: ticket?.id || ticket?.uuid,
        property: "penalty",
        value: calculatedPenalty,
      });
      updateTicketViolationType({
        variables: {
          input: {
            incidentId: incidentId ? incidentId : incidentUuid,
            ticketId: ticket?.id ? ticket?.id : ticket?.uuid,
            originatorId: user?.id,
            officerId: ticket?.officer?.id,
            penaltyMax: +container?.maxPenalty,
            penaltyMin: +container?.minPenalty,
            waiver: +container?.waiver,
            points: +container?.points,
            penalty: calculatedPenalty,
          },
        },
        context: { serializationKey: "incidentQueue" },
      });
    } else {
      // console.log('CV values already updated')
    }
  };

  useEffect(() => {
    if (
      displayCommercialFields(
        ticket?.ticketViolationPrototype?.ticketCategory?.name,
      ) &&
      commercialViolationContainers?.length > 0
    ) {
      if (ticket?.weightLimit && ticket?.violationOccurrence) {
        const result = commercialViolationContainers.filter(function (v) {
          return (
            v["ticketViolationTypeId"]?.toString() ===
              ticket?.ticketViolationPrototype?.id?.toString() &&
            v["weightLimitId"]?.toString() ===
              ticket?.weightLimit?.id?.toString() &&
            v["violationOccurrenceId"]?.toString() ===
              ticket?.violationOccurrence?.id?.toString()
          );
        });
        if (result?.length > 1) {
          alert("Multiple commercial vehicle container matches found");
        }
        // console.log('updateCvTicket useEffect')
        result?.length > 0 && updateCvTicket(result[0]);
      } else {
        // console.log('CV ticket but weightLimit and occurrence not set')
      }
    } else {
      // console.log('No CV violation')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ticket?.ticketViolationPrototype,
    ticket?.weightLimit,
    ticket?.violationOccurrence,
    commercialViolationContainers,
  ]);

  useEffect(() => {
    if (
      ticket?.currentStatus &&
      ticket?.currentStatus < 3 &&
      ticket?.issuedTo?.id
    ) {
      const pisBac = piss?.find(
        specific => specific.person?.id === ticket?.issuedTo?.id,
      )?.bac;
      if (pisBac && ticket?.bacValue !== pisBac) {
        handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
          incidentId: incidentId || incidentUuid,
          ticketId: ticket?.id || ticket?.uuid,
          property: "bacValue",
          value: pisBac,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentIndex, piss, ticket.issuedTo, ticket?.id, ticket.currentStatus]);

  useEffect(() => {
    if (
      ticket?.currentStatus &&
      ticket?.currentStatus < 3 &&
      !ticket?.issuedTo?.id &&
      ticket?.bacValue
    ) {
      handleDispatch(UPDATE_INCIDENT_TICKET, UPDATE_TICKET, {
        incidentId: incidentId || incidentUuid,
        ticketId: ticket?.id || ticket?.uuid,
        property: "bacValue",
        value: "",
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    incidentIndex,
    piss,
    ticket.issuedTo,
    ticket?.id,
    ticket?.uuid,
    ticket.currentStatus,
  ]);

  const toggleWarningCheckbox = value => {
    setLocalWarningValue(value);
    let newViolation = null;
    if (ticket?.ticketViolationPrototype?.fullName) {
      if (value) {
        newViolation = ticketViolationWarnings
          .concat(municipalWarnings)
          .find(v => v.fullName === ticket?.ticketViolationPrototype?.fullName);
      } else {
        newViolation = ticketViolations
          .concat(municipalViolations)
          .find(v => v.fullName === ticket?.ticketViolationPrototype?.fullName);
      }
    }
    handleChange({ target: { name: "ticketNumber", value: "" } }, true);
    updateLocalTicketViolation(newViolation, "ticketViolationPrototype", value);
  };

  const showErrorMessages =
    validating &&
    !localWarningValue &&
    !isTicketWarning(ticket?.ticketViolationPrototype);

  const violationOutcomeErrorMessage =
    showErrorMessages && !validationTest.violationMatchesOutcome
      ? 'The ticket outcome is set to "W - Warning" but the violation is a Ticket'
      : "";

  if (onlineState || !onlineState) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          maxWidth: "100%",
          justifyContent: "flex-start",
          border: "1px solid grey",
          padding: inIncident ? "inherit" : "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <TextField
            disabled={memoizedDisabled || ticket?.currentStatus > 2}
            data-cy="ticketNumber"
            inputProps={{ "data-cy": "ticketNumber-input" }}
            InputLabelProps={{
              shrink: true,
              "data-cy": "ticketNumber-input-label",
            }}
            margin="dense"
            id="ticketNumber-id"
            label={"Ticket\xa0Number"}
            value={ticket?.ticketNumber || ""}
            name="ticketNumber"
            className={classes.textInput}
            onChange={e => handleChange(e, false)}
            onBlur={e => handleChange(e, true)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.target.blur();
              }
            }}
          />
          {/* {numberValidating && canSubmitToJb && !validationTest.ticketNumberIsValid && <FormHelperText className={clsx(classes.leftMarginMatch, classes.redLabel)}>The {localWarningValue ? "warning" : "ticket"} number must start with {localWarningValue ? "W" : "7"} followed by 6 digits</FormHelperText>} */}
          {numberValidating &&
            !localWarningValue &&
            canSubmitToJb &&
            !validationTest.ticketNumberIsValid && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                E-Ticket numbers are automatically created. Please leave this
                field blank.
              </FormHelperText>
            )}
          {numberValidating &&
            !canSubmitToJb &&
            !validationTest.ticketNumberIsValid && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                {localWarningValue
                  ? "The warning number must start with a W followed by six digits"
                  : "The ticket number cannot be blank"}
              </FormHelperText>
            )}
        </div>
        {/* This will be updated with a state value. When the checkbox is checked or unchecked, clear the ticketViolation */}
        {/* updateLocalTicketViolation("", "ticketViolationPrototype") - should clear it */}
        <CheckboxWithLabel
          disabled={memoizedDisabled}
          checked={localWarningValue}
          stateName={"isWarning"}
          onChange={e => {
            toggleWarningCheckbox(e.target.checked);
          }}
          title={"Warning"}
          formStyle={classes.formControl}
          userRoles={userRoles}
          {...props}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          {/* {ticketViolationsLoading */}
          {/* ? <VirtualizedAutocomplete disabled={memoizedDisabled} title={'Violation'} loading={ticketViolationsLoading} options={[{ id: "-1", fullName: "Loading the data" }]} error={ticketViolationsError} value={{ id: "-1", fullName: "Loading ticket options" }} /> */}
          {/* :  */}
          <VirtualizedAutocomplete
            customClass={
              !ticket?.ticketViolationPrototype?.fullName && !localWarningValue
                ? classes.vcvc_required
                : !ticket?.ticketViolationPrototype?.fullName &&
                  classes.warning_recommended
            }
            disabled={memoizedDisabled}
            title={"Violation"}
            loading={false}
            options={
              localWarningValue
                ? ticketViolationWarnings.concat(municipalWarnings)
                : ticketViolations.concat(municipalViolations)
            }
            error={null}
            value={ticket?.ticketViolationPrototype}
            updateFields={updateLocalTicketViolation}
            stateName={"ticketViolationPrototype"}
            oriId={incidentOriId}
          />
          {/* } */}
          {showErrorMessages && !validationTest.violationSelected && (
            <FormHelperText
              className={clsx(classes.leftMarginMatch, classes.redLabel)}
            >
              This value is required
            </FormHelperText>
          )}
          {showErrorMessages && !validationTest.violationMatchesOutcome && (
            <FormHelperText
              className={clsx(classes.leftMarginMatch, classes.redLabel)}
            >
              {violationOutcomeErrorMessage}
            </FormHelperText>
          )}
          {showErrorMessages && !validationTest?.ticketHasNonZeroWaiver && (
            <FormHelperText
              className={clsx(classes.leftMarginMatch, classes.redLabel)}
            >
              The waiver amount must be greater than 0. Please reselect the
              violation.
            </FormHelperText>
          )}
        </div>
        <TextField
          className={classes.smallMatchMargin}
          disabled={true}
          data-cy="ticketType"
          inputProps={{
            "data-cy": "ticketType-input",
            className: classes.disabled,
          }}
          InputLabelProps={{
            "data-cy": "ticketType-input-label",
            shrink: true,
          }}
          margin="dense"
          id="ticketType-id"
          label={"Ticket\xa0Type"}
          value={ticket?.ticketViolationPrototype?.ticketEntry?.name || ""}
          name="ticketType"
        />
        <TextField
          className={classes.smallMatchMargin}
          disabled={true}
          data-cy="ticketCategory"
          inputProps={{
            "data-cy": "ticketCategory-input",
            className: classes.disabled,
          }}
          InputLabelProps={{
            "data-cy": "ticketCategory-input-label",
            shrink: true,
          }}
          margin="dense"
          id="ticketCategory-id"
          label={"Ticket\xa0Category"}
          value={ticket?.ticketViolationPrototype?.ticketCategory?.name || ""}
          name="ticketCategory"
        />
        <TextField
          className={classes.smallMatchMargin}
          disabled={true}
          data-cy="ticketSubCategory"
          inputProps={{
            "data-cy": "ticketSubCategory-input",
            className: classes.disabled,
          }}
          InputLabelProps={{
            "data-cy": "ticketSubCategory-input-label",
            shrink: true,
          }}
          margin="dense"
          id="ticketSubCategory-id"
          label={"Ticket\xa0Sub\xa0Category"}
          value={
            ticket?.ticketViolationPrototype?.ticketSubCategory?.name || ""
          }
          name="ticketCategory"
        />
        {displayFedVio(
          ticket?.ticketViolationPrototype?.ticketCategory?.name,
        ) && (
          <>
            <FormControl variant="standard" className={classes.formControl}>
              <CustomAutoComplete
                disabled={
                  isSuperUser(userRoles)
                    ? false
                    : memoizedLocked
                      ? true
                      : ticket?.currentStatus > 2
                        ? true
                        : false
                }
                title={"Federal\xa0Violation\xa0Code"}
                options={federalViolationCodes}
                value={
                  ticket?.federalViolationCode
                    ? ticket?.federalViolationCode
                    : {}
                }
                stateName={"federalViolationCode"}
                updateFields={handleAutoCompleteChange}
                userRoles={userRoles}
                {...props}
              />
              {showErrorMessages && !validationTest.federalViolationCode && (
                <FormHelperText
                  className={clsx(classes.leftMarginMatch, classes.redLabel)}
                >
                  This value is required
                </FormHelperText>
              )}
            </FormControl>

            <CheckboxWithLabel
              disabled={
                memoizedLocked || ticket?.currentStatus > 2 ? true : false
              }
              checked={ticket?.cvHazardFee || false}
              stateName={"cvHazardFee"}
              onChange={handleChange}
              title={"Hazard\xa0Fee"}
              formStyle={classes.formControl}
              userRoles={userRoles}
              {...props}
            />
          </>
        )}
        {displayCommercialFields(
          ticket?.ticketViolationPrototype?.ticketCategory?.name,
        ) && (
          <>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              <SingleDropDown
                disabled={memoizedDisabled}
                handleChange={handleChange}
                stateName={"weightLimit"}
                value={ticket?.weightLimit}
                options={weightLimits}
                title={"Weight Limit"}
                classStyle={clsx(classes.formControl, classes.fixedWidth)}
                inputLabel={inputLabel}
                labelWidth={labelWidth}
                userRoles={userRoles}
                {...props}
              />
              {showErrorMessages && !validationTest.overweightWeight && (
                <FormHelperText
                  className={clsx(classes.leftMarginMatch, classes.redLabel)}
                >
                  This value is required
                </FormHelperText>
              )}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              <SingleDropDown
                disabled={memoizedDisabled}
                handleChange={handleChange}
                stateName={"violationOccurrence"}
                value={ticket?.violationOccurrence}
                options={violationOccurrences}
                title={"Occurrence"}
                classStyle={clsx(classes.formControl, classes.fixedWidth)}
                inputLabel={inputLabel}
                labelWidth={labelWidth}
                userRoles={userRoles}
                {...props}
              />
              {showErrorMessages && !validationTest.overweightOccurrence && (
                <FormHelperText
                  className={clsx(classes.leftMarginMatch, classes.redLabel)}
                >
                  This value is required
                </FormHelperText>
              )}
            </div>
          </>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "end",
          }}
        >
          <SingleDropDown
            disabled={true}
            stateName={"area"}
            value={incidents[incidentIndex]?.area}
            options={areas}
            title={"Area"}
            classStyle={clsx(classes.formControl, classes.vcvcfixedWidth)}
            // classStyle={clsx(classes.formControl, classes.fixedWidth)}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
          />
          <SingleDropDown
            disabled={memoizedDisabled}
            handleChange={handleChange}
            stateName={"highway"}
            value={ticket?.highway}
            options={highways}
            title={"Highway"}
            classStyle={clsx(classes.formControl, classes.vcvcfixedWidth)}
            // classStyle={clsx(classes.formControl, classes.fixedWidth)}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- Focus the violation location when it needs validation attention. */}
            <AddressInputAutoSuggest autoFocus={showErrorMessages && !validationTest.violationLocation}
              disabled={memoizedDisabled}
              address={ticketLocation}
              id={ticket?.id || null}
              setAddress={(address, updateStatus) =>
                updateAddress(address, updateStatus, "ticketLocation")
              }
              updateType={"ticket"}
              title={"Vio. Location (Place/Landmark)"}
              textfieldClass={classes.textfieldMarginAndFlexEnd}
              customClass={
                !ticketLocation?.streetName && !localWarningValue
                  ? classes.vcvc_required
                  : !ticketLocation?.streetName && classes.warning_recommended
              }
              userRoles={userRoles}
              pauseLocationPolling={() => setPausePolling(true)}
              resumeLocationPolling={() => setPausePolling(false)}
              {...props}
            />
            {showErrorMessages && !validationTest.violationLocation && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                This value is required
              </FormHelperText>
            )}
            {showErrorMessages &&
              validationTest.violationLocation &&
              !validationTest.violationLocationIsComplete && (
                <FormHelperText
                  className={clsx(classes.leftMarginMatch, classes.redLabel)}
                >
                  The address is not complete
                </FormHelperText>
              )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <CustomAutoComplete
              title={"Municipality"}
              options={municipalities}
              addMargin={true}
              customClass={
                !ticket?.municipality && !localWarningValue
                  ? classes.vcvc_required
                  : null
              }
              value={ticket?.municipality || null}
              stateName={"municipality"}
              disabled={memoizedDisabled}
              updateFields={handleAutoCompleteChange}
              nameFormatter={false}
              minWidth={"150px"}
              userRoles={user?.roleNames}
              {...props}
            />
            {showErrorMessages && !validationTest.municipality && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                This value is required
              </FormHelperText>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- Focus the violation date when it needs validation attention. */}
            <WrappedKeyboardDateTimePicker autoFocus={showErrorMessages && !validationTest.violationDateInPast}
              ampm={false}
              className={clsx(
                classes.smallMatchMargin,
                !ticket?.violationDate
                  ? !localWarningValue
                    ? classes.vcvc_required
                    : classes.warning_recommended
                  : null,
              )}
              dataCy="violation-date-time"
              disabled={memoizedDisabled}
              disableFuture={true}
              format="MM/DD/YYYY HH:mm"
              InputLabelProps={{ shrink: true }}
              label="Vio Date"
              maxDateMessage={"The violation date cannot be in the future"}
              name="violationDate"
              onBlur={() => setPausePolling(false)}
              onChange={handleDateChange}
              onFocus={() => setPausePolling(true)}
              showTodayButton={true}
              value={parseDateFlexible(ticket?.violationDate)}
            />
            {showErrorMessages && !validationTest.violationDateInPast && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                The violation date cannot be in the future
              </FormHelperText>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <SingleDropDown
              disabled={memoizedDisabled}
              handleChange={handleChange}
              stateName={"ticketDeliveryType"}
              value={ticket?.ticketDeliveryType}
              options={ticketDeliveryTypes}
              title={"Delivered\xa0By"}
              classStyle={clsx(
                classes.smallMatchMargin,
                !ticket?.ticketDeliveryType
                  ? !localWarningValue
                    ? classes.vcvc_required
                    : classes.warning_recommended
                  : null,
              )}
              inputLabel={inputLabel}
              labelWidth={labelWidth}
              userRoles={userRoles}
              {...props}
            />
            {showErrorMessages && !validationTest.deliveredBy && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                This value is required
              </FormHelperText>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/no-autofocus -- Focus the recipient selector when it needs validation attention. */}
            <SingleDropDown autoFocus={
              (showErrorMessages && !validationTest.issuedTo) ||
              (showErrorMessages && validationTest.issuedTo && !validationTest.validDob)
            }
              disabled={memoizedDisabled}
              handleChange={handleChange}
              stateName={"issuedTo"}
              value={ticket?.issuedTo}
              options={incidentPeople}
              title={"Issued\xa0To"}
              classStyle={clsx(
                classes.smallMatchMargin,
                classes.fixedWidth,
                !ticket?.issuedTo
                  ? !localWarningValue
                    ? classes.vcvc_required
                    : classes.warning_recommended
                  : null,
              )}
              inputLabel={inputLabel}
              labelWidth={labelWidth}
              nameFormatter={ticket?.name ? null : formatNameFromPersonObj}
              userRoles={userRoles}
              {...props}
            />
            {showErrorMessages && !validationTest.issuedTo && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                This value is required
              </FormHelperText>
            )}
            {showErrorMessages &&
              validationTest.issuedTo &&
              !validationTest.validLastName && (
                <FormHelperText
                  className={clsx(classes.leftMarginMatch, classes.redLabel)}
                >
                  Last name is blank.
                </FormHelperText>
              )}
            {validating &&
              validationTest.issuedTo &&
              !validationTest.validDob && (
                <FormHelperText
                  className={clsx(classes.leftMarginMatch, classes.redLabel)}
                >
                  The date of birth for this person is not valid
                </FormHelperText>
              )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <WrappedKeyboardDateTimePicker
              ampm={false}
              className={clsx(
                classes.smallMatchMargin,
                !ticket?.issuedAt
                  ? !localWarningValue
                    ? classes.vcvc_required
                    : classes.warning_recommended
                  : null,
              )}
              dataCy="violation-date-time"
              disabled={memoizedDisabled}
              format="MM/DD/YYYY HH:mm"
              InputLabelProps={{ shrink: true }}
              label="Served Date"
              name="issuedAt"
              onBlur={() => setPausePolling(false)}
              onChange={handleDateChange}
              onFocus={() => setPausePolling(true)}
              showTodayButton={true}
              value={parseDateFlexible(ticket?.issuedAt)}
            />
            {showErrorMessages && !validationTest.dateServed && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                This value is required
              </FormHelperText>
            )}
            {showErrorMessages && !validationTest.dateServedIsAfterVioDate && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                The served date must be after the violation date
              </FormHelperText>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <SingleDropDown
              disabled={memoizedDisabled}
              handleChange={handleChange}
              stateName={"issuedToAddress"}
              value={ticket?.issuedToAddress}
              options={personAddresses}
              title={"Mailing\xa0Address"}
              classStyle={clsx(
                classes.formControl,
                classes.fixedWidth,
                !ticket?.issuedToAddress
                  ? !localWarningValue
                    ? classes.vcvc_required
                    : classes.warning_recommended
                  : null,
              )}
              inputLabel={inputLabel}
              labelWidth={labelWidth}
              userRoles={userRoles}
              {...props}
            />
            {showErrorMessages && !validationTest.mailingAddress && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                This value is required
              </FormHelperText>
            )}
            {showErrorMessages &&
              validationTest.mailingAddress &&
              !validationTest.mailingAddressIsComplete && (
                <FormHelperText
                  className={clsx(classes.leftMarginMatch, classes.redLabel)}
                >
                  The address is not complete
                </FormHelperText>
              )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <CheckboxWithLabel
              disabled={memoizedDisabled}
              checked={ticket?.mailingAddressVerified || false}
              stateName={"mailingAddressVerified"}
              onChange={handleChange}
              title={"Valid\xa0Mailing\xa0Address"}
              formStyle={clsx(
                classes.formControl,
                !ticket?.mailingAddressVerified && !localWarningValue
                  ? classes.vcvc_required
                  : null,
              )}
              userRoles={userRoles}
              {...props}
            />
            {showErrorMessages && !validationTest.validMailingAddress && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                This value is required
              </FormHelperText>
            )}
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <SingleDropDown
              disabled={memoizedDisabled}
              handleChange={handleChange}
              stateName={"officer"}
              value={ticket?.officer}
              options={incidentOfficers}
              title={"Issued\xa0By"}
              classStyle={clsx(
                classes.formControl,
                classes.fixedWidth,
                !ticket?.officer
                  ? !localWarningValue
                    ? classes.vcvc_required
                    : classes.warning_recommended
                  : null,
              )}
              inputLabel={inputLabel}
              labelWidth={labelWidth}
              nameFormatter={formatOfficerName}
              userRoles={userRoles}
              {...props}
            />
            {showErrorMessages && !validationTest.officerStateIdIsPresent && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                The State ID number of the issuing officer must not be blank
              </FormHelperText>
            )}
            {showErrorMessages &&
              validationTest.officerStateIdIsPresent &&
              !validationTest.officerStateIdIsValid && (
                <FormHelperText
                  className={clsx(classes.leftMarginMatch, classes.redLabel)}
                >
                  The State ID number of the issuing officer must be a capital
                  letter followed by 4 digits
                </FormHelperText>
              )}
          </div>
          <TextField
            disabled={memoizedDisabled}
            data-cy="code"
            inputProps={{ "data-cy": "code-input" }}
            InputLabelProps={{ "data-cy": "code-input-label", shrink: true }}
            margin="dense"
            id="code-id"
            label={"Code"}
            value={ticket?.code || ""}
            name="code"
            className={classes.textfieldMarginAndFlexEnd}
            onChange={e => handleChange(e, false)}
            onBlur={e => handleChange(e, true)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.target.blur();
              }
            }}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <TextField
              disabled={memoizedDisabled}
              data-cy="actualSpeed"
              InputProps={{
                "data-cy": "actualSpeed-input",
                endAdornment: (
                  <InputAdornment position="end">MPH</InputAdornment>
                ),
              }}
              InputLabelProps={{
                "data-cy": "actualSpeed-input-label",
                shrink: true,
              }}
              margin="dense"
              id="actualSpeed-id"
              label={"Actual\xa0Speed"}
              value={ticket?.actualSpeed || ""}
              type={"number"}
              name="actualSpeed"
              className={clsx(
                classes.textfieldMarginAndFlexEnd,
                ticket?.ticketViolationPrototype?.speedingVio &&
                  !ticket?.actualSpeed
                  ? classes.vcvc_required
                  : null,
              )}
              onChange={e => handleChange(e, false)}
              onBlur={e => handleChange(e, true)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.target.blur();
                }
              }}
            />
            {showErrorMessages && !validationTest.actualSpeedAbovePosted && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                The actual speed must be greater than the posted limit
              </FormHelperText>
            )}
            {showErrorMessages && !validationTest.actualSpeedPresent && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                The actual speed must be present and greater than zero
              </FormHelperText>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <TextField
              disabled={memoizedDisabled}
              data-cy="postedLimit"
              InputProps={{
                "data-cy": "postedLimit-input",
                endAdornment: (
                  <InputAdornment position="end">MPH</InputAdornment>
                ),
              }}
              InputLabelProps={{
                "data-cy": "postedLimit-input-label",
                shrink: true,
              }}
              margin="dense"
              id="postedLimit-id"
              label={"Posted\xa0Limit"}
              value={ticket?.postedLimit || ""}
              type={"number"}
              name="postedLimit"
              className={clsx(
                classes.textfieldMarginAndFlexEnd,
                ticket?.ticketViolationPrototype?.speedingVio &&
                  !ticket?.postedLimit
                  ? classes.vcvc_required
                  : null,
              )}
              onChange={e => handleChange(e, false)}
              onBlur={e => handleChange(e, true)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.target.blur();
                }
              }}
            />
            {showErrorMessages && !validationTest.postedSpeedPresent && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                The posted limit must be present and greater than zero
              </FormHelperText>
            )}
          </div>
          <TextField
            disabled={true}
            data-cy="waiver"
            inputProps={{
              "data-cy": "waiver-input",
              className: classes.disabled,
            }}
            InputLabelProps={{ "data-cy": "waiver-input-label", shrink: true }}
            margin="dense"
            id="waiver-id"
            label={"Waiver"}
            // value={calculatePenalty(ticket?.actualSpeed, ticket?.postedLimit, ticket?.waiver, ticket?.points, ticket?.penaltyMin, ticket?.penaltyMax, ticket?.cvHazardFee, ticket?.seatBeltOccurrence, localWarningValue) || ""}
            value={ticket?.penalty || ""}
            name="waiver"
            className={classes.textfieldMarginAndFlexEnd}
          />
          <TextField
            disabled={true}
            data-cy="points"
            inputProps={{
              "data-cy": "points-input",
              className: classes.disabled,
            }}
            InputLabelProps={{ "data-cy": "points-input-label", shrink: true }}
            margin="dense"
            id="points-id"
            label={"Points"}
            value={ticket?.points || ""}
            name="points"
            className={classes.textfieldMarginAndFlexEnd}
          />
          <TextField
            disabled={true}
            data-cy="penaltyMin"
            inputProps={{
              "data-cy": "penaltyMin-input",
              className: classes.disabled,
            }}
            InputLabelProps={{
              "data-cy": "penaltyMin-input-label",
              shrink: true,
            }}
            margin="dense"
            id="penaltyMin-id"
            label={"Min\xa0Penalty"}
            value={ticket?.penaltyMin || ""}
            name="penaltyMin"
            className={classes.textfieldMarginAndFlexEnd}
          />
          <TextField
            disabled={true}
            data-cy="penaltyMax"
            inputProps={{
              "data-cy": "penaltyMax-input",
              className: classes.disabled,
            }}
            InputLabelProps={{
              "data-cy": "penaltyMax-input-label",
              shrink: true,
            }}
            margin="dense"
            id="penaltyMax-id"
            label={"Max\xa0Penalty"}
            value={ticket?.penaltyMax || ""}
            name="penaltyMax"
            className={classes.textfieldMarginAndFlexEnd}
          />
          <SingleDropDown
            disabled={memoizedLocked}
            handleChange={e => handleChange(e, true, false)}
            stateName={"ticketStatus"}
            value={ticket?.ticketStatus}
            options={ticketStatuses}
            title={"Ticket\xa0Status"}
            classStyle={clsx(classes.formControl, classes.fixedWidth)}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            userRoles={userRoles}
            {...props}
          />
          <TextField
            disabled={true}
            data-cy="bac"
            inputProps={{ "data-cy": "bac-input", className: classes.disabled }}
            InputLabelProps={{ "data-cy": "bac-input-label", shrink: true }}
            margin="dense"
            id="bac-id"
            label={"BAC"}
            // value={piss?.find(specific => specific.person?.id === ticket?.issuedTo?.id)?.bac || ""}
            value={ticket?.bacValue || ""}
            name="bac"
            className={classes.textfieldMarginAndFlexEnd}
          />
          <TextField
            disabled={memoizedLocked}
            data-cy="dispositionPenaltyAmount"
            inputProps={{ "data-cy": "dispositionPenaltyAmount-input" }}
            InputLabelProps={{
              "data-cy": "dispositionPenaltyAmount-input-label",
              shrink: true,
            }}
            margin="dense"
            id="dispositionPenaltyAmount-id"
            label={"Disposition\xa0Penalty\xa0Amount"}
            value={ticket?.dispositionPenaltyAmount || ""}
            name="dispositionPenaltyAmount"
            className={classes.textfieldMarginAndFlexEnd}
            onChange={e => handleChange(e, false, false)}
            onBlur={e => handleChange(e, true, false)}
            type={"number"}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.target.blur();
              }
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "end",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <DefendantDidThen
              nightMode={nightMode}
              memoizedDisabled={memoizedDisabled}
              defendantActions={ticket?.defendantActions}
              handleChange={handleChange}
              formControl={clsx(
                classes.formControl,
                !ticket?.defendantActions
                  ? !localWarningValue
                    ? classes.vcvc_required
                    : classes.warning_recommended
                  : null,
              )}
            />

            {showErrorMessages && !validationTest.defendantDidThen && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                This value is required
              </FormHelperText>
            )}
          </div>
          <LicenseNumberAndState
            handleChange={null}
            state={state}
            stateOptions={states}
            userRoles={userRoles}
            driversLicense={ticket?.driversLicense}
            disabled={true}
            canEdit={false}
            directToPersonFrame={
              showErrorMessages && !validationTest.validLicenseNumber
            }
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <CheckboxWithLabel
              disabled={memoizedDisabled}
              checked={ticket?.cdl || false}
              stateName={"cdl"}
              onChange={handleChange}
              title={"CDL"}
              formStyle={classes.formControl}
              userRoles={userRoles}
              {...props}
            />
            {showErrorMessages && !validationTest.cdlOrganization && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                CDL can not be set when the defendant is an organization
              </FormHelperText>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <SingleDropDown
              disabled={memoizedDisabled}
              handleChange={handleChange}
              stateName={"reliefActType"}
              value={ticket?.reliefActType}
              options={reliefActTypes}
              title={`Civ.\xa0Serv.\xa0Member's\xa0Relief\xa0Act`}
              classStyle={clsx(
                classes.formControl,
                classes.fixedWidth,
                !ticket?.reliefActType && !localWarningValue
                  ? classes.vcvc_required
                  : null,
              )}
              inputLabel={inputLabel}
              labelWidth={labelWidth}
              userRoles={userRoles}
              {...props}
            />
            {showErrorMessages && !validationTest.reliefActType && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                This value is required
              </FormHelperText>
            )}
            {showErrorMessages && !validationTest.under17ReliefActType && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                Defendant must be under 17 years of age on the violation date
              </FormHelperText>
            )}
            {showErrorMessages && !validationTest.under17OnlyReliefActType && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                Select the under-17 relief act option when the defendant was
                under 17 on the violation date
              </FormHelperText>
            )}
            {showErrorMessages && !validationTest.organizationReliefActType && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                Select the organization or corporation option for a business
              </FormHelperText>
            )}
            {showErrorMessages && !validationTest.individualReliefActType && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                The organization or corporation option is only valid for a
                business
              </FormHelperText>
            )}
          </div>
          <TextField
            disabled={memoizedDisabled}
            data-cy="reliefActComment"
            inputProps={{ "data-cy": "reliefActComment-input" }}
            InputLabelProps={{
              "data-cy": "reliefActComment-input-label",
              shrink: true,
            }}
            margin="dense"
            id="reliefActComment-id"
            label={"Relief\xa0Act\xa0Comment"}
            value={ticket?.reliefActComment || ""}
            name="reliefActComment"
            className={clsx(classes.textfieldMarginAndFlexEnd)}
            onChange={e => handleChange(e, false)}
            onBlur={e => handleChange(e, true)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.target.blur();
              }
            }}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <SingleDropDown
              disabled={memoizedDisabled}
              handleChange={handleChange}
              stateName={"defendantParent"}
              value={ticket?.defendantParent}
              options={
                incidentPeople?.filter(
                  person => person.id !== ticket?.issuedTo?.id,
                ) || []
              }
              title={"Defendant\xa0parent"}
              classStyle={clsx(
                classes.formControl,
                juvenile && !ticket?.defendantParent && !localWarningValue
                  ? classes.vcvc_required
                  : null,
              )}
              inputLabel={inputLabel}
              labelWidth={labelWidth}
              nameFormatter={formatNameFromPersonObj}
              userRoles={userRoles}
              {...props}
            />
            {showErrorMessages &&
              !validationTest.parentAddressPresentOrNotRequired && (
                <FormHelperText
                  className={clsx(classes.leftMarginMatch, classes.redLabel)}
                >
                  The parent address is not present
                </FormHelperText>
              )}
            {showErrorMessages &&
              validationTest.parentAddressPresentOrNotRequired &&
              !validationTest.parentAddressCompleteOrNotRequired && (
                <FormHelperText
                  className={clsx(classes.leftMarginMatch, classes.redLabel)}
                >
                  The parent address is not complete
                </FormHelperText>
              )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <SingleDropDown
              disabled={memoizedDisabled}
              handleChange={handleChange}
              stateName={"deliveredToType"}
              value={ticket?.deliveredToType}
              options={deliveredToTypes}
              title={"Delivered\xa0To"}
              classStyle={clsx(
                classes.formControl,
                classes.fixedWidth,
                !ticket?.deliveredToType
                  ? !localWarningValue
                    ? classes.vcvc_required
                    : classes.warning_recommended
                  : null,
              )}
              inputLabel={inputLabel}
              labelWidth={labelWidth}
              userRoles={userRoles}
              {...props}
            />
            {showErrorMessages && !validationTest.deliveredTo && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                This value is required
              </FormHelperText>
            )}
          </div>
          <TextField
            disabled={memoizedDisabled}
            data-cy="municipalOrdinance"
            inputProps={{ "data-cy": "municipalOrdinance-input" }}
            InputLabelProps={{
              "data-cy": "municipalOrdinance-input-label",
              shrink: true,
            }}
            margin="dense"
            id="municipalOrdinance-id"
            label={"Municipal\xa0Ord."}
            value={ticket?.municipalOrdinance || ""}
            name="municipalOrdinance"
            className={classes.textfieldMarginAndFlexEnd}
            onChange={e => handleChange(e, false)}
            onBlur={e => handleChange(e, true)}
            onKeyDown={e => {
              if (e.key === "Enter") {
                e.target.blur();
              }
            }}
          />
          <SingleDropDown
            disabled={memoizedLocked}
            handleChange={e => handleChange(e, true, false)}
            stateName={"operator"}
            value={ticket?.operator}
            options={incidentPeople}
            title={"Operator"}
            classStyle={clsx(classes.formControl, classes.fixedWidth)}
            inputLabel={inputLabel}
            labelWidth={labelWidth}
            nameFormatter={formatNameFromPersonObj}
            userRoles={userRoles}
            {...props}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
            }}
          >
            <SingleDropDown
              disabled={memoizedDisabled}
              handleChange={handleChange}
              stateName={"vehicle"}
              value={ticket?.vehicle}
              options={vehicles}
              title={"Vehicle"}
              classStyle={clsx(classes.formControl, classes.fixedWidth)}
              inputLabel={inputLabel}
              labelWidth={labelWidth}
              nameFormatter={formatNameFromVehicleObj}
              userRoles={userRoles}
              {...props}
            />
            {showErrorMessages && !validationTest.cmvHasVehicle && (
              <FormHelperText
                className={clsx(classes.leftMarginMatch, classes.redLabel)}
              >
                Commercial vehicle is checked. Please add a vehicle to the
                ticket.
              </FormHelperText>
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
              border: "1px solid grey",
            }}
          >
            <SingleDropDown
              disabled={memoizedDisabled}
              handleChange={handleChange}
              stateName={"seatBeltOccurrence"}
              value={
                ticket?.seatBeltOccurrence
                  ? {
                      id: ticket?.seatBeltOccurrence,
                      name: ticket?.seatBeltOccurrence,
                    }
                  : ""
              }
              options={[
                { id: "First", name: "First" },
                { id: "Second", name: "Second" },
                { id: "Third", name: "Third" },
              ]}
              title={"Seatbelt\xa0Vio.\xa0Occur"}
              name={"name"}
              classStyle={clsx(classes.formControl, classes.fixedWidth)}
              inputLabel={inputLabel}
              labelWidth={labelWidth}
              userRoles={userRoles}
              {...props}
            />
            <CheckboxWithLabel
              disabled={memoizedDisabled}
              checked={ticket?.accident || false}
              stateName={"accident"}
              onChange={handleChange}
              title={"Accident"}
              formStyle={classes.formControl}
              userRoles={userRoles}
              {...props}
            />
            <CheckboxWithLabel
              disabled={memoizedDisabled}
              checked={ticket?.hazmat || false}
              stateName={"hazmat"}
              onChange={handleChange}
              title={"HazMat"}
              formStyle={classes.formControl}
              userRoles={userRoles}
              {...props}
            />
            <CheckboxWithLabel
              disabled={memoizedDisabled}
              checked={ticket?.fatality || false}
              stateName={"fatality"}
              onChange={handleChange}
              title={"Fatality"}
              formStyle={classes.formControl}
              userRoles={userRoles}
              {...props}
            />
            <CheckboxWithLabel
              disabled={memoizedDisabled}
              checked={ticket?.commercialVehicle || false}
              stateName={"commercialVehicle"}
              onChange={handleChange}
              title={"Commercial\xa0Vehicle"}
              formStyle={classes.formControl}
              userRoles={userRoles}
              {...props}
            />
            <CheckboxWithLabel
              disabled={memoizedDisabled}
              checked={ticket?.relativeCrimeCharge || false}
              stateName={"relativeCrimeCharge"}
              onChange={handleChange}
              title={"Rel.\xa0Crim.\xa0Charge"}
              formStyle={classes.formControl}
              userRoles={userRoles}
              {...props}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "row-wrap",
              alignItems: "center",
              width: "100%",
            }}
          >
            <FormControl
              variant="standard"
              style={{ width: "50%" }}
              className={classes.formControl}
            >
              <FormLabel component="label">{"Officer\xa0Notes"}</FormLabel>
              <TextareaAutosize
                className={
                  nightMode ? classes.textAreaDark : classes.textAreaLight
                }
                disabled={memoizedLocked}
                data-cy="narrative"
                style={{
                  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                  fontSize: "1rem",
                  width: "100%",
                  maxWidth: "100%",
                }}
                margin="dense"
                minRows={3}
                id="narrative-id"
                value={ticket?.narrative || ""}
                name="narrative"
                onChange={e => handleChange(e, false, false)}
                onBlur={e => handleChange(e, true, false)}
              />
            </FormControl>
            {/* Add updater */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                flexWrap: "row-wrap",
                alignItems: "center",
                width: "50%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-around",
                    width: "100%",
                  }}
                >
                  <SingleDropDown
                    disabled={memoizedLocked}
                    handleChange={e => handleChange(e, true, false)}
                    stateName={"trespassRequestor"}
                    value={ticket?.trespassRequestor}
                    options={incidentPeople}
                    title={"Trespass\xa0Notice\xa0Requested\xa0By"}
                    classStyle={clsx(classes.formControl, classes.fixedWidth)}
                    inputLabel={inputLabel}
                    labelWidth={labelWidth}
                    nameFormatter={formatNameFromPersonObj}
                    userRoles={userRoles}
                    {...props}
                  />
                  <FormControl
                    variant="standard"
                    className={classes.formControl}
                  >
                    <KeyboardDatePicker
                      disabled={memoizedLocked}
                      data-cy="trespassUntil-date-picker"
                      InputLabelProps={{ shrink: true }}
                      clearable
                      showTodayButton
                      label="Trespass Until"
                      value={
                        ticket?.trespassUntil
                          ? moment
                              .utc(
                                ticket?.trespassUntil,
                                "YYYY-MM-DD HH:mm:ss UTC",
                              )
                              .local()
                          : null
                      }
                      onChange={(date, value) =>
                        handleDateChange(value, "trespassUntil", date, false)
                      }
                      format="MM/DD/YYYY"
                      ampm={false}
                    />
                  </FormControl>
                </div>
                {/* <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}> */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-around",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      alignItems: "center",
                      fontSize: "1rem",
                      fontWeight: "bold",
                      textAlign: "center",
                      width: "100%",
                      color: nightMode ? "white" : "black",
                    }}
                  >
                    Conditions
                  </div>
                  <MultiDropDownObjects
                    disabled={memoizedLocked}
                    id={"weatherConditions"}
                    formStyle={classes.formControl}
                    labelStyle={classes.multiInputLabel}
                    title={"Weather"}
                    selectStyle={null}
                    chipsStyle={classes.chips}
                    chipStyle={classes.chip}
                    stateName={"weatherConditions"}
                    //this handleChange might have to change to a multiChange
                    handleChange={handleTicketConditionChange}
                    selectedValues={
                      ticket?.ticketCondition?.weatherConditions
                        ? weatherConditions.filter(option =>
                            ticket?.ticketCondition?.weatherConditions?.some(
                              type => option.id === type?.id,
                            ),
                          )
                        : []
                    }
                    options={
                      weatherConditions.length > 0 ? weatherConditions : []
                    }
                    userRoles={userRoles}
                    {...props}
                  />
                  <MultiDropDownObjects
                    disabled={memoizedLocked}
                    id={"roadConditions"}
                    formStyle={classes.formControl}
                    labelStyle={classes.multiInputLabel}
                    title={"Road"}
                    selectStyle={null}
                    chipsStyle={classes.chips}
                    chipStyle={classes.chip}
                    stateName={"roadConditions"}
                    //this handleChange might have to change to a multiChange
                    handleChange={handleTicketConditionChange}
                    selectedValues={
                      ticket?.ticketCondition?.roadConditions
                        ? roadConditions.filter(option =>
                            ticket?.ticketCondition?.roadConditions?.some(
                              type => option.id === type?.id,
                            ),
                          )
                        : []
                    }
                    options={roadConditions.length > 0 ? roadConditions : []}
                    userRoles={userRoles}
                    {...props}
                  />
                  <MultiDropDownObjects
                    disabled={memoizedLocked}
                    id={"trafficConditions"}
                    formStyle={classes.formControl}
                    labelStyle={classes.multiInputLabel}
                    title={"Traffic"}
                    selectStyle={null}
                    chipsStyle={classes.chips}
                    chipStyle={classes.chip}
                    stateName={"trafficConditions"}
                    //this handleChange might have to change to a multiChange
                    handleChange={handleTicketConditionChange}
                    selectedValues={
                      ticket?.ticketCondition?.trafficConditions
                        ? trafficConditions.filter(option =>
                            ticket?.ticketCondition?.trafficConditions?.some(
                              type => option.id === type?.id,
                            ),
                          )
                        : []
                    }
                    options={
                      trafficConditions.length > 0 ? trafficConditions : []
                    }
                    userRoles={userRoles}
                    {...props}
                  />
                  <MultiDropDownObjects
                    disabled={memoizedLocked}
                    id={"lightingConditions"}
                    formStyle={classes.formControl}
                    labelStyle={classes.multiInputLabel}
                    title={"Light"}
                    selectStyle={null}
                    chipsStyle={classes.chips}
                    chipStyle={classes.chip}
                    stateName={"lightingConditions"}
                    //this handleChange might have to change to a multiChange
                    handleChange={handleTicketConditionChange}
                    selectedValues={
                      ticket?.ticketCondition?.lightingConditions
                        ? lightingConditions.filter(option =>
                            ticket?.ticketCondition?.lightingConditions?.some(
                              type => option.id === type?.id,
                            ),
                          )
                        : []
                    }
                    options={
                      lightingConditions.length > 0 ? lightingConditions : []
                    }
                    userRoles={userRoles}
                    {...props}
                  />
                </div>
              </div>
            </div>
          </div>
          <SpeedMeasurement
            locked={memoizedLocked}
            userRoles={userRoles}
            inIncident={inIncident}
            ticketId={ticket?.id || ticket?.uuid}
            incidentId={incidentId}
            {...props}
          />
          <RaceDataUof
            locked={memoizedLocked}
            ticket={ticket}
            handleChange={handleChange}
            userRoles={userRoles}
            outcomeOfStopError={violationOutcomeErrorMessage}
            {...props}
          />
        </div>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-around"
          style={{ width: "100%", paddingTop: "3vh", paddingBottom: "3vh" }}
        >
          {inIncident && (
            <Button
              disabled={memoizedLocked}
              variant="contained"
              onClick={() =>
                handleRemoveTicket(ticket?.id ?? null, ticket?.uuid ?? null)
              }
            >
              Delete Ticket
            </Button>
          )}
          {inIncident && (
            <Button
              disabled={memoizedLocked || addTicketDisabled}
              variant="contained"
              onClick={() => handleAddTicketToIncident(false)}
            >
              Add Ticket
            </Button>
          )}
          {inIncident && (
            <Button
              disabled={memoizedLocked || addTicketDisabled}
              variant="contained"
              onClick={() => handleAddTicketToIncident(true)}
            >
              Duplicate Ticket
            </Button>
          )}
          <Button
            disabled={
              memoizedLocked ||
              (!onlineState &&
                !user?.startingTicketNumber &&
                !ticket?.ticketNumber)
            }
            variant="contained"
            onClick={() => openPrintTicketModal()}
          >
            Print Preview
          </Button>
          <SubmitButton
            memoizedLocked={memoizedLocked}
            online={onlineState}
            user={user}
            handleTicketSubmission={handleTicketSubmission}
            setPopper={setPopper}
            ticket={ticket}
            localWarningValue={localWarningValue}
          />
        </Box>
        <Modal
          open={showPrintModal}
          onClose={() => setShowPrintModal(false)}
          className={classes.ticketModal}
          data-cy="ticket-print-modal"
          keepMounted={false}
        >
          <div
            style={{
              height: "75%",
              width: "fit-content",
              backgroundColor: "white",
              overflowY: "scroll",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: nightMode ? "#616161" : "#fae8ad",
                borderRadius: "5px",
                padding: "0 5px",
                alignItems: "center",
              }}
            >
              <h3 style={{ color: nightMode ? "white" : "inherit" }}>
                {isTicketWarning(ticket?.ticketViolationPrototype)
                  ? "Warning"
                  : "VCVC"}
              </h3>
              <CloseIcon
                onClick={() => setShowPrintModal(false)}
                className={classes.hover}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                name="print"
                aria-label="print button"
                onClick={async () => {
                  const { success, error, message } = await handlePrint();
                  if (success) {
                    setPopper({
                      open: true,
                      content: message
                        ? message
                        : "Your ticket has been printed successfully.",
                      severity: "success",
                    });
                  } else {
                    setPopper({
                      open: true,
                      content: error?.message,
                      severity: "error",
                    });
                  }
                }}
                className={clsx(classes.buttonHover, classes.navBarButtons)}
                data-cy="print-button"
                variant="contained"
              >
                Print
              </Button>
            </div>
            <>
              <VCVC
                ticket={ticket}
                showDialog={true}
                bac={ticket?.bacValue || ""}
                defendantCopy={true}
                isWarning={isTicketWarning(ticket?.ticketViolationPrototype)}
              />
              {/* <div style={{ display: "none" }}>
                <VCVC
                  ticket={ticket}
                  showDialog={true}
                  bac={ticket?.bacValue || ""}
                  defendantCopy={true}
                  isWarning={isTicketWarning(ticket?.ticketViolationPrototype)}
                />
              </div> */}
              <div style={{ display: "none" }}>
                <VCVC
                  ref={componentRef}
                  ticket={ticket}
                  showDialog={false}
                  bac={ticket?.bacValue || ""}
                  defendantCopy={true}
                  isWarning={isTicketWarning(ticket?.ticketViolationPrototype)}
                />
              </div>
            </>
          </div>
        </Modal>
      </div>
    );
  } else {
    return <div>Loading ticket information...</div>;
  }
};

function SubmitButton(props) {
  let shouldBeDisabled =
    !props.online ||
    props.memoizedLocked ||
    isTicketWarning(props.ticket?.ticketViolationPrototype) ||
    props.localWarningValue ||
    (props.ticket.currentStatus >= 4 && !isSuperUser(props.user?.roleNames)) ||
    !checkRoles(props.user?.roleNames, "submit_to_jb");

  return (
    <Button
      disabled={shouldBeDisabled}
      variant="contained"
      onClick={async () => {
        const { success, error, message } =
          await props.handleTicketSubmission();
        if (success) {
          props.setPopper({
            open: true,
            content: message,
            severity: "success",
          });
        } else {
          props.setPopper({
            open: true,
            content: `There was an issue submitting the ticket: ${error?.message}`,
            severity: "error",
          });
        }
      }}
    >
      Submit
    </Button>
  );
}

export default TicketAndWarning;
